import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import Confetti from 'react-confetti';
import 'react-toastify/dist/ReactToastify.css';

import api from '../../config/axiosConfig';
import { API_URLS, STATSTYPE, EightMeterFoul } from '../../config/Constants';
import { sessionManager } from '../../config/sessionManager';
import { showErrorMsg, showSuccessMsg } from '../../config/common';
import { getHoursSinceGameStart } from '../../utils/dateUtils';
import QuarterSelector from './QuarterSelector';
import PenaltiesSection from './Stats/PenaltiesSection';
import EightMeterSection from './Stats/EightMeterSection';
import EightMeterFoulModal from './Stats/EightMeterFoulModal';
import CapturePlayground from './Stats/CapturePlayground';
import CardColorModal from './Stats/CardColorModal';
import PossessionTimesSection from './Stats/PossessionTimesSection';
import ShotsSection from './Stats/ShotsSection';
import GroundBallsSection from './Stats/GroundBallsSection';
import TurnoversSection from './Stats/TurnoversSection';
import ClearsSection from './Stats/ClearsSection';
import FaceOffSection from './Stats/FaceOffSection';
import StatsList from './Stats/StatsList';
import PlayerSelectionModal from './Stats/PlayerSelectionModal';
import GoalieSelectModal from './Stats/GoalieSelectModal';
import PenaltyModal from './Stats/PenaltyModal';
import AddStatModal from './Stats/AddStatModal';
import AssistPromptModal from './Stats/AssistPromptModal';
import ShotClockViolationModal from './Stats/ShotClockViolationModal';
import DataEntryQuarterWarningModal from './Stats/DataEntryQuarterWarningModal';
import { PickedLocation } from '../../components/shotChart/ShotLocationPicker';
import ThemShotLocationSheet from '../../components/shotChart/ThemShotLocationSheet';
import { ShotLocation, buildShotLocation } from '../../utils/shotLocation';
import useShotLocationCapture from '../../hooks/useShotLocationCapture';
import { isShotChartClient } from '../../utils/shotChartClient';

// Add a window size hook
function useWindowSize() {
  const [size, setSize] = useState({
    width: window.innerWidth,
    height: window.innerHeight,
    scrollY: window.scrollY,
  });

  useEffect(() => {
    const handleResize = () =>
      setSize({
        width: window.innerWidth,
        height: window.innerHeight,
        scrollY: window.scrollY,
      });
    const handleScroll = () => setSize((s) => ({ ...s, scrollY: window.scrollY }));
    window.addEventListener('resize', handleResize);
    window.addEventListener('scroll', handleScroll);
    return () => {
      window.removeEventListener('resize', handleResize);
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);
  return size;
}

type StatsRefreshFilters = {
  playerId?: string;
  statType?: string;
};

type StatsRefreshOptions = {
  skipPossessionTimes?: boolean;
  preserveActiveTimer?: boolean;
};

const describeStat = (item: any) => {
  if (item?.type === 'Penalty Start' && item.cardColor === 'yellow') return 'Yellow Card';
  if (item?.type === 'Penalty Start' && item.cardColor === 'red') return 'Red Card';
  if (item?.type === 'Penalty End' && item.cardColor === 'yellow') return 'Yellow Card End';
  if (item?.type === 'Penalty End' && item.cardColor === 'red') return 'Red Card End';
  if (item?.type === 'Faceoff Won') return 'Draw Control Won';
  if (item?.type === 'Faceoff Lost') return 'Draw Control Lost';
  if (item?.type === '8M Free Possession' && Array.isArray(item.fouls) && item.fouls.length > 0) {
    return `8M Free Possession (${item.fouls.join(', ')})`;
  }
  const base = item?.type || item?.desc || '';
  if (item?.eightMeter && base !== '8M Free Possession') return `8M ${base}`;
  return base;
};

const SHOT_STAT_TYPES = ['Shot Goal', 'Shot Saved', 'Shot Missed', 'Shot Blocked'];
const GOALIE_STAT_TYPES = ['Shot not saved', 'Shot Was Saved By', 'Was Blocked By'];
// Any of these ends the 8M possession sequence
const EIGHT_METER_CLOSING_TYPES = [...SHOT_STAT_TYPES, 'Turnover', 'Shot Clock Violation'];

const isEightMeterShotEntry = (entry: any, side: 'US' | 'THEM') => {
  if (side === 'US') return entry.player === 'us' && SHOT_STAT_TYPES.includes(entry.type);
  return (
    (entry.player === 'them' && SHOT_STAT_TYPES.includes(entry.type)) ||
    (entry.player === 'us' && GOALIE_STAT_TYPES.includes(entry.type))
  );
};

const RED_CARD_MINUTES = 5;

type PendingStatSubmission = {
  entries: any[];
  quarter: string;
  filters?: StatsRefreshFilters;
  refreshOptions?: StatsRefreshOptions;
};

const StatsPage: React.FC = () => {
  const gameId = sessionManager.getGameId();
  const clientId = sessionManager.getClientId();
  const seasonId = sessionManager.getSeasonId();

  // Custom hooks
  const { width, height } = useWindowSize();

  // Detect if device is touch-only (mobile/tablet) vs touch-enabled laptop/desktop
  const isTouchOnly = (() => {
    // Check if device has touch capability
    const hasTouch = 'ontouchstart' in window || navigator.maxTouchPoints > 0;

    // Check if it's a mobile/tablet user agent (not a laptop with touchscreen)
    const mobileRegex =
      /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini|Mobile|mobile|CriOS|Tablet|PlayBook|Silk|Kindle/i;
    const isMobileUA = mobileRegex.test(navigator.userAgent);

    // Only return true if BOTH touch-capable AND mobile user agent
    return hasTouch || isMobileUA;
  })();

  const [players, setPlayers] = useState<Array<any>>([]);
  const [activeQuarter, setActiveQuarter] = useState(() => {
    // Initialize activeQuarter from sessionManager, fallback to "Q1"
    return sessionManager.getActiveQuarter() || null;
  });
  const [hoveredTheyShot, setHoveredTheyShot] = useState<string | null>(null);
  const [actionFeedback, setActionFeedback] = useState<string | null>(null);
  const [curGoalStatus, setCurGoalStatus] = useState(false);
  const [showConfetti, setShowConfetti] = useState(false);
  const [usGoalClicked, setUsGoalClicked] = useState(false);
  const [themGoalClicked, setThemGoalClicked] = useState(false);
  const pendingIsTransitionRef = useRef(false);
  const currentPossOutcomeRef = useRef<{ us: 'goal' | 'turnover' | 'shot' | null; them: 'goal' | 'turnover' | 'shot' | null }>({
    us: null,
    them: null,
  });

  const notePossessionOutcome = (player: string, type: string) => {
    const team = player === 'us' || player === 'them' ? player : null;
    if (!team) return;
    const rank = { goal: 3, turnover: 2, shot: 1 };
    let outcome: 'goal' | 'turnover' | 'shot' | null = null;
    if (type === 'Shot Goal') outcome = 'goal';
    else if (type === 'Turnover' || type === 'Shot Clock Violation') outcome = 'turnover';
    else if (type === 'Shot Saved' || type === 'Shot Missed' || type === 'Shot Blocked') outcome = 'shot';
    if (!outcome) return;
    const current = currentPossOutcomeRef.current[team];
    if (!current || rank[outcome] > rank[current]) {
      currentPossOutcomeRef.current[team] = outcome;
    }
  };

  const consumePossessionOutcome = (team: 'us' | 'them') => {
    const outcome = currentPossOutcomeRef.current[team];
    currentPossOutcomeRef.current[team] = null;
    return outcome || undefined;
  };

  // New shot modal states
  const [showShotModal, setShowShotModal] = useState(false);
  const [shotModalType, setShotModalType] = useState<'goal' | 'saved' | 'missed' | 'blocked' | null>(null);
  const [shotModalSide, setShotModalSide] = useState<'US' | 'THEM' | null>(null);
  const [selectedShotPlayer, setSelectedShotPlayer] = useState<number | null>(null);

  // Shot Location & Zone Mapping — always skippable, and the whole field can be turned off
  const [shotLocationCaptureEnabled] = useShotLocationCapture();
  const [shotLocationValue, setShotLocationValue] = useState<PickedLocation | null>(null);
  // Opponent shots: the location sheet opens first, then the normal outcome flow
  // (goalie / blocker) runs and attaches the location when the stat is saved.
  const [themLocationType, setThemLocationType] = useState<'goal' | 'saved' | 'missed' | 'blocked' | null>(null);
  // True after Skip on the zone sheet, so the goalie / blocker modal still shows the field.
  const [themLocationDeferred, setThemLocationDeferred] = useState(false);
  const themShotLocationRef = useRef<ShotLocation | null>(null);
  // Set synchronously so a second tap cannot open another shot screen while
  // the opponent location sheet is up.
  const themLocationOpenRef = useRef(false);
  const takeThemShotLocation = () => {
    const shotLocation = themShotLocationRef.current;
    themShotLocationRef.current = null;
    return shotLocation ? { shotLocation } : {};
  };
  // Location for the shot being saved from the player-selection modal: ours
  // comes from the picker inside the modal, theirs from the location sheet.
  const pickedLocationFields = () => {
    if (!shotLocationValue) return null;
    themShotLocationRef.current = null;
    return { shotLocation: buildShotLocation(shotLocationValue.x, shotLocationValue.y, 'live') };
  };
  const shotModalLocationFields = () => {
    if (shotModalSide === 'THEM') return pickedLocationFields() || takeThemShotLocation();
    if (shotModalSide === 'US' && shotLocationCaptureEnabled && shotLocationValue) {
      return { shotLocation: buildShotLocation(shotLocationValue.x, shotLocationValue.y, 'live') };
    }
    return {};
  };
  const [showAssistModal, setShowAssistModal] = useState(false);
  const [selectedAssistPlayerForGoal, setSelectedAssistPlayerForGoal] = useState<number | null>(null);

  // Assist prompt modal state
  const [showAssistPromptModal, setShowAssistPromptModal] = useState(false);
  const [goalScorerForAssistPrompt, setGoalScorerForAssistPrompt] = useState<number | string | null>(null);

  const [statsData, setStatsData] = useState([]);
  const [periods, setPeriods] = useState<string[]>([]);
  const [gameDate, setGameDate] = useState<string | null>(null); // For stats lock
  const [gameTime, setGameTime] = useState<string | null>(null); // For stats lock
  const [gameTimezone, setGameTimezone] = useState<string | null>(null); // For stats lock

  // Goalie selection (declared early — used by getGameInformation / start prompt)
  const [currentGoalieId, setCurrentGoalieId] = useState<string | null>(null);
  const [seasonDefaultGoalieId, setSeasonDefaultGoalieId] = useState<string | null>(null);
  const [gameGoalieLoaded, setGameGoalieLoaded] = useState(false);
  const [showChangeGoalieModal, setShowChangeGoalieModal] = useState(false);
  const [selectedChangeGoalie, setSelectedChangeGoalie] = useState<string | null>(null);
  const [isStartGoaliePrompt, setIsStartGoaliePrompt] = useState(false);
  const goalieStartPromptCheckedRef = useRef(false);

  const [usTime, setUsTime] = useState(0);
  const [eightMeterSide, setEightMeterSide] = useState<'US' | 'THEM' | null>(null);
  const eightMeterSideRef = useRef<'US' | 'THEM' | null>(null);
  // 8M capture flow: THEM picks who fouled, both sides pick the foul, then a gold confirmation
  const [eightMeterFlowSide, setEightMeterFlowSide] = useState<'US' | 'THEM' | null>(null);
  const [showEightMeterFoulerModal, setShowEightMeterFoulerModal] = useState(false);
  const [showEightMeterFoulModal, setShowEightMeterFoulModal] = useState(false);
  const [eightMeterFoulerId, setEightMeterFoulerId] = useState<string | number | null>(null);
  // Red-carded players are ejected for the rest of the game
  const [ejectedPlayerIds, setEjectedPlayerIds] = useState<string[]>([]);
  const [themTime, setThemTime] = useState(0);
  const [activeTimer, setActiveTimer] = useState<'US' | 'THEM' | null>(null);
  const [glowButton, setGlowButton] = useState<'US' | 'THEM' | 'NONE' | null>(null);
  const statMutationChainRef = useRef(Promise.resolve());
  const selectionSubmittingRef = useRef(false);
  const [selectionSubmitting, setSelectionSubmitting] = useState(false);
  const directActionLocksRef = useRef<Set<string>>(new Set());
  const [directActionLocks, setDirectActionLocks] = useState<Record<string, boolean>>({});

  /** Serialize stat mutations so rapid stat taps still save in order. */
  const enqueueStatMutation = useCallback(async (fn: () => Promise<void>) => {
    const prev = statMutationChainRef.current;
    let release!: () => void;
    const gate = new Promise<void>((resolve) => {
      release = resolve;
    });
    statMutationChainRef.current = prev.then(() => gate);
    await prev;
    try {
      await fn();
    } finally {
      release();
    }
  }, []);

  const runWithSelectionSubmitLock = useCallback(async (fn: () => Promise<void>) => {
    if (selectionSubmittingRef.current) {
      return;
    }

    selectionSubmittingRef.current = true;
    setSelectionSubmitting(true);

    try {
      await fn();
    } finally {
      selectionSubmittingRef.current = false;
      setSelectionSubmitting(false);
    }
  }, []);

  const runWithDirectActionLock = useCallback(async (actionKey: string, fn: () => Promise<void>) => {
    if (directActionLocksRef.current.has(actionKey)) {
      return;
    }

    directActionLocksRef.current.add(actionKey);
    setDirectActionLocks((prev) => ({ ...prev, [actionKey]: true }));

    try {
      await fn();
    } finally {
      directActionLocksRef.current.delete(actionKey);
      setDirectActionLocks((prev) => {
        const next = { ...prev };
        delete next[actionKey];
        return next;
      });
    }
  }, []);

  // Track last start time and value for US and THEM
  const [usLastStart, setUsLastStart] = useState<{
    value: number;
    timestamp: number;
  } | null>(null);
  const [themLastStart, setThemLastStart] = useState<{
    value: number;
    timestamp: number;
  } | null>(null);

  // Update formatTime to show [minutes:seconds.milliseconds] format
  const formatTime = (t: number) => {
    const totalSeconds = t;
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = Math.floor(totalSeconds % 60);
    const milliseconds = Math.floor((totalSeconds % 1) * 1000);

    return `${minutes.toString().padStart(2, '0')}:${seconds
      .toString()
      .padStart(2, '0')}.${milliseconds.toString().padStart(3, '0')}`;
  };

  // Function to get shot modal subtitle based on type and side
  const getShotModalSubtitle = (
    shotType: 'goal' | 'saved' | 'missed' | 'blocked' | null,
    shotSide: 'US' | 'THEM' | null,
  ) => {
    if (shotType === 'goal') {
      return shotSide === 'US' ? 'Who shot the goal?' : 'Who was the goalkeeper?';
    } else if (shotType === 'saved') {
      return shotSide === 'US' ? 'Who shot?' : 'Who made the save?';
    } else if (shotType === 'missed') {
      return 'Who shot and missed?';
    } else if (shotType === 'blocked') {
      return shotSide === 'US' ? 'Who shot?' : 'Who blocked their shot?';
    } else {
      return `Select player for ${shotType} shot`;
    }
  };

  // Function to get turnover modal subtitle based on type and side
  const getTurnoverModalSubtitle = (
    turnoverType: 'onWeGroundBall' | 'onWeStoleBall' | 'onWeTurnover' | 'onTheyStoleBall' | null,
    turnoverSide: 'US' | 'THEM' | null,
  ) => {
    if (turnoverSide === 'US') {
      if (turnoverType === 'onWeGroundBall') {
        return 'Who picked up the ball?';
      } else if (turnoverType === 'onWeStoleBall') {
        return 'Who took the ball?';
      } else if (turnoverType === 'onWeTurnover') {
        return 'Who lost the ball?';
      } else {
        return 'Who made the play?';
      }
    } else {
      if (turnoverType === 'onTheyStoleBall') {
        return 'Who lost the ball?';
      } else {
        return 'Who forced the turnover?';
      }
    }
  };

  // Quarter data state
  const [gameTrackingData, setGameTrackingData] = useState({
    penalties: false,
    possessionTime: false,
    shots: false,
    groundBalls: false,
    turnovers: false,
    clears: false,
    faceOffs: false,
  });
  const [loading, setLoading] = useState(false);

  // Check if game is locked (more than 24 hours from start time) - timezone-aware
  const isGameLocked = useMemo(() => {
    if (!gameDate) return false;

    try {
      const hoursSinceGame = getHoursSinceGameStart({
        date: gameDate,
        time: gameTime || undefined,
        timezone: gameTimezone || undefined,
      });

      return hoursSinceGame > 24;
    } catch (e) {
      console.error('Error checking if game is locked:', e);
      return false;
    }
  }, [gameDate, gameTime, gameTimezone]);

  // Check if user can bypass stats lock
  const canBypassStatsLock = sessionManager.canPerformAction('games.gameMenu.statsLockAfterTimeout', 'edit');

  // Determine if stat taker buttons should be hidden
  const hideStatTakerButtons = isGameLocked && !canBypassStatsLock;

  useEffect(() => {
    const fetchUserTrackingRoles = async () => {
      if (!activeQuarter) return;

      try {
        setLoading(true);
        const user = sessionManager.getUser();
        const userId = user?.id;

        if (!userId) {
          showErrorMsg('User session not found');
          setLoading(false);
          return;
        }

        const response = await api.get(API_URLS.GET_USER_TRACKING_ROLES, {
          params: { userId, seasonId, clientId },
        });

        if (response.data.success) {
          const data = response.data.data;
          const turnoversEnabled = !!data.turnovers;
          // Older roles only had combined turnovers; keep GBs enabled with them until reconfigured
          const groundBallsEnabled =
            data.groundBalls !== undefined ? !!data.groundBalls : turnoversEnabled;
          const userTrackingRoles = {
            penalties: data.penalties || false,
            possessionTime: data.possessionTime || false,
            shots: data.shots || false,
            groundBalls: groundBallsEnabled,
            turnovers: turnoversEnabled,
            clears: data.clears || false,
            faceOffs: data.faceOffs || false,
          };
          setGameTrackingData(userTrackingRoles);
        } else {
          showErrorMsg(response.data.message || 'Failed to fetch tracking roles');
        }
      } catch (error: any) {
        showErrorMsg('Failed to fetch tracking roles. Please try again.');
      } finally {
        setLoading(false);
      }
    };

    fetchUserTrackingRoles();
  }, [activeQuarter, seasonId, clientId]);

  useEffect(() => {
    getPlayer();

    const initPeriods = async () => {
      if (!clientId || !seasonId) return;
      try {
        // Fetch season type and game info sequentially so OT is set exactly once
        const [seasonRes, gameRes] = await Promise.all([
          api.get(`${API_URLS.GET_SEASON_PERIOD}`, {
            params: { clientId, seasonId },
          }),
          gameId ? api.get(`${API_URLS.GET_GAME_BY_ID}/${gameId}`) : Promise.resolve(null),
        ]);

        if (!seasonRes.data.success) {
          showErrorMsg(seasonRes.data.message || 'Failed to fetch season period settings');
          return;
        }

        const hp = !!seasonRes.data.data.halfPeriod;
        const hasOT = !!(gameRes?.data?.success && gameRes.data.data?.otFlag);

        const basePeriods: string[] = hp ? ['H1', 'H2'] : ['Q1', 'Q2', 'Q3', 'Q4'];
        const finalPeriods = hasOT ? [...basePeriods, 'OT'] : basePeriods;
        setPeriods(finalPeriods);

        const validQuarters = hasOT ? [...basePeriods, 'OT'] : basePeriods;
        const currentQuarter = sessionManager.getActiveQuarter();
        if (!currentQuarter || !validQuarters.includes(currentQuarter)) {
          updateActiveQuarter(basePeriods[0]);
        }
      } catch (error: any) {
        showErrorMsg('Failed to fetch season period settings');
      }
    };
    initPeriods();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    handleRefreshStats();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientId, seasonId, gameId, activeQuarter]);

  // Timer effect
  useEffect(() => {
    let interval: NodeJS.Timeout | null = null;
    if (activeTimer === 'US') {
      interval = setInterval(() => setUsTime((t) => +(t + 0.1).toFixed(3)), 100);
    } else if (activeTimer === 'THEM') {
      interval = setInterval(() => setThemTime((t) => +(t + 0.1).toFixed(3)), 100);
    }
    return () => {
      if (interval) clearInterval(interval);
    };
  }, [activeTimer]);

  // Function to fetch possession times
  const fetchPossessionTimes = async (options?: { preserveActiveTimer?: boolean }) => {
    if (gameId && clientId && seasonId && activeQuarter) {
      try {
        const response = await api.post(API_URLS.POSSESSION_TIME_GET, {
          gameId,
          clientId,
          seasonId: seasonId,
          period: activeQuarter,
        });
        const preserveActiveTimer = options?.preserveActiveTimer !== false;
        if (response.data) {
          const nextUsTime = response.data.us ? Number(response.data?.us) : 0;
          const nextThemTime = response.data.them ? Number(response.data?.them) : 0;

          // While a possession timer is actively running, keep that side's local live
          // value instead of overwriting it with the last persisted server total.
          if (!preserveActiveTimer || activeTimer !== 'US') {
            setUsTime(nextUsTime);
          }

          if (!preserveActiveTimer || activeTimer !== 'THEM') {
            setThemTime(nextThemTime);
          }
        } else {
          if (!options?.preserveActiveTimer || activeTimer !== 'US') {
            setUsTime(0);
          }
          if (!options?.preserveActiveTimer || activeTimer !== 'THEM') {
            setThemTime(0);
          }
        }
      } catch (err) {
        if (!options?.preserveActiveTimer || activeTimer !== 'US') {
          setUsTime(0);
        }
        if (!options?.preserveActiveTimer || activeTimer !== 'THEM') {
          setThemTime(0);
        }
      }
    }
  };

  // Fetch quarter data when quarter changes
  useEffect(() => {
    // Stop timer and reset timer values when quarter changes
    setActiveTimer(null);
    setUsLastStart(null);
    setThemLastStart(null);
    fetchPossessionTimes({ preserveActiveTimer: false });

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeQuarter]);

  useEffect(() => {
    // Fetch only game metadata (date/time/timezone for stats lock)
    getGameInformation();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gameId]);

  // Fetch season data to check shot clock setting
  useEffect(() => {
    const fetchSeasonData = async () => {
      if (!seasonId || !clientId) return;

      try {
        const response = await api.get(`${API_URLS.GET_SEASON_BY_ID}/${seasonId}`, {
          params: { clientId },
        });

        if (response.data.success && response.data.data) {
          const seasonData = response.data.data;
          setShotClockEnabled(!!seasonData.shotClock);
        } else {
          setShotClockEnabled(false);
        }
      } catch (error) {
        console.error('Error fetching season data:', error);
        setShotClockEnabled(false);
      }
    };

    fetchSeasonData();
  }, [seasonId, clientId]);

  const getGameInformation = async () => {
    if (!gameId) {
      return;
    }

    try {
      const [gameRes, seasonRes] = await Promise.all([
        api.get(`${API_URLS.GET_GAME_BY_ID}/${gameId}`, {
          params: { clientId },
        }),
        seasonId
          ? api.get(`${API_URLS.GET_SEASON_BY_ID}/${seasonId}`, { params: { clientId } })
          : Promise.resolve(null),
      ]);

      if (gameRes.data.success && gameRes.data.data) {
        if (gameRes.data.data.date) {
          setGameDate(gameRes.data.data.date);
        }
        if (gameRes.data.data.time) {
          setGameTime(gameRes.data.data.time);
        }
        if (gameRes.data.data.timezone) {
          setGameTimezone(gameRes.data.data.timezone);
        }
        setCurrentGoalieId(gameRes.data.data.currentGoalieId || null);
      } else {
        showErrorMsg(gameRes.data.message || 'Failed to get the game information');
      }

      if (seasonRes?.data?.success && seasonRes.data.data) {
        setSeasonDefaultGoalieId(seasonRes.data.data.startingGoalieId || null);
      }
      setGameGoalieLoaded(true);
    } catch (error: any) {
      showErrorMsg('Failed to get the game information');
      setGameGoalieLoaded(true);
    }
  };

  const getPlayer = async () => {
    try {
      if (!clientId) {
        showErrorMsg('Please select a game');
        return;
      }

      const response = await api.get(
        `${API_URLS.GET_GAME_PLAYER}?clientId=${clientId}&seasonId=${seasonId}&gameId=${gameId}`,
      );

      if (response.data.success && response.data?.data?.length > 0) {
        setPlayers(response.data.data);
      }
    } catch (err: any) {
      console.error('Error fetching players:', err);
    }
  };

  // Function to calculate current game scores from stats data
  // const calculateGameScores = (allStats: any[]) => {
  //   let usGoals = 0;
  //   let themGoals = 0;

  //   allStats.forEach((stat) => {
  //     if (stat.type === "Shot Goal") {
  //       if (stat.player === "us") {
  //         usGoals++;
  //       } else if (stat.player === "them") {
  //         themGoals++;
  //       }
  //     }
  //   });

  //   return { us: usGoals, them: themGoals };
  // };

  // Function to check if we're at the end of final regular period and game is tied
  // const checkForTiedGame = async () => {
  //   if (hasCheckedTiedGame) return; // Don't check if already checked

  //   // Determine if we're at the end of the final regular period
  //   const isFinalRegularPeriod = halfPeriod
  //     ? activeQuarter === "H2"
  //     : activeQuarter === "Q4";

  //   if (!isFinalRegularPeriod) return;

  //   const scores = calculateGameScores(statsData);
  //   setGameScores(scores);

  //   // Check if game is tied
  //   if (scores.us === scores.them) {
  //     setShowTiedGamePrompt(true);
  //   }
  // };

  // Function to refresh stats data
  const handleRefreshStats = async (filters?: StatsRefreshFilters, options?: StatsRefreshOptions): Promise<void> => {
    if (!clientId) {
      showErrorMsg('Please select a Client');
      return;
    } else if (!seasonId) {
      showErrorMsg('Please select a Season');
      return;
    } else if (!gameId) {
      showErrorMsg('Please select a Game');
      return;
    }

    // Don't fetch if activeQuarter is not set yet
    if (!activeQuarter) {
      return;
    }

    try {
      const params: any = {
        clientId,
        seasonId,
        gameId,
        period: activeQuarter,
      };
      if (filters) {
        if (filters.playerId) params.playerId = filters.playerId;
        if (filters.statType) params.statstype = filters.statType;
      } else {
        if (playerFilter) params.playerId = playerFilter;
        if (statTypeFilter) params.statstype = statTypeFilter;
      }

      const response = await api.get(API_URLS.GET_ALL_STATS, { params });
      if (response.data && response.data.success && Array.isArray(response.data.data)) {
        // Map backend data to StatRow[]
        const mapped = response.data.data.map((item: any) => ({
          _id: item._id,
          time: item.time || '',
          desc: describeStat(item),
          team: item.playerName || item.player || '',
          green: item.green ?? false,
          playerId: item.playerId,
          isTransition: !!item.isTransition,
          outcome: item.outcome,
          // Add new player details
          firstName: item.firstName,
          lastName: item.lastName,
          playerNumber: item.playerNumber,
          playerName: item.playerName,
        }));
        setStatsData(mapped);

        // Update game scores for display purposes
        // const scores = calculateGameScores(response.data.data);
        // setGameScores(scores);

        // Load active penalties from the stats response
        loadActivePenaltiesFromResponse(response.data.activePenalties || []);
        setEjectedPlayerIds((response.data.ejectedPlayerIds || []).map(String));

        // Refresh possession times to update button displays
        if (!options?.skipPossessionTimes) {
          await fetchPossessionTimes();
        }
      } else {
        setStatsData([]);
        showErrorMsg(response.data.message || 'Failed to fetch stats data');
      }
    } catch (err) {
      setStatsData([]);
      showErrorMsg('Failed to fetch stats data');
    }
  };

  // Function to load active penalties from the stats API response
  const loadActivePenaltiesFromResponse = (activePenaltiesData: any[]) => {
    try {
      const usPenalties: any[] = [];
      const themPenalties: any[] = [];

      activePenaltiesData.forEach((penaltyStart: any) => {
        const penalty: any = {
          id: penaltyStart.id || penaltyStart.statId,
          statId: penaltyStart.statId,
          playerId: penaltyStart.playerId,
          time: new Date(penaltyStart.time).toLocaleTimeString([], {
            hour: '2-digit',
            minute: '2-digit',
            second: '2-digit',
            hour12: false,
          }),
          player: null,
          label: 'Unknown',
          cardColor: penaltyStart.cardColor === 'yellow' || penaltyStart.cardColor === 'red' ? penaltyStart.cardColor : null,
          periodLabel: null, // NEW: Separate field for period indicator
          openedInPeriod: penaltyStart.period,
          isCarriedOver: penaltyStart.period !== activeQuarter,
        };

        if (penaltyStart.player === 'us') {
          // Use player details from the response
          if (
            penaltyStart.playerId &&
            penaltyStart.playerId !== 'us' &&
            penaltyStart.playerId !== -1 &&
            penaltyStart.playerId !== -2
          ) {
            if (penaltyStart.firstName && penaltyStart.lastName && penaltyStart.playerNumber) {
              penalty.player = {
                _id: penaltyStart.playerId,
                firstName: penaltyStart.firstName,
                lastName: penaltyStart.lastName,
                no: penaltyStart.playerNumber,
              };
              penalty.label = `${penaltyStart.playerNumber}, ${penaltyStart.firstName} ${penaltyStart.lastName[0]}.`;
            } else {
              // Fallback to players array lookup
              const player = players.find((p) => p._id === penaltyStart.playerId);
              if (player) {
                penalty.player = player;
                penalty.label = `${player.no}, ${player.firstName} ${player.lastName[0]}.`;
              } else {
                penalty.label = 'US Player';
              }
            }
          } else {
            penalty.label = 'US';
          }

          // Set period indicator separately for next line display
          if (penalty.isCarriedOver) {
            penalty.periodLabel = `(FROM ${penaltyStart.period})`;
          }

          usPenalties.push(penalty);
        } else if (penaltyStart.player === 'them') {
          penalty.label = 'Them';

          // Set period indicator separately for next line display
          if (penalty.isCarriedOver) {
            penalty.periodLabel = `(FROM ${penaltyStart.period})`;
          }

          themPenalties.push(penalty);
        }
      });

      setUsPenalty(usPenalties);
      setThemPenalty(themPenalties);
    } catch (error) {
      console.error('Error loading active penalties:', error);
      setUsPenalty([]);
      setThemPenalty([]);
    }
  };

  const applyStatsRefreshPayload = (responseData: any, options?: StatsRefreshOptions) => {
    if (!responseData || !Array.isArray(responseData.data)) {
      setStatsData([]);
      showErrorMsg(responseData?.message || 'Failed to fetch stats data');
      return;
    }

    const mapped = responseData.data.map((item: any) => ({
      _id: item._id,
      time: item.time || '',
          desc: describeStat(item),
          team: item.playerName || item.player || '',
          green: item.green ?? false,
          playerId: item.playerId,
          isTransition: !!item.isTransition,
          outcome: item.outcome,
          firstName: item.firstName,
      lastName: item.lastName,
      playerNumber: item.playerNumber,
      playerName: item.playerName,
      shotLocation: item.shotLocation || null,
    }));

    setStatsData(mapped);
    loadActivePenaltiesFromResponse(responseData.activePenalties || []);
    setEjectedPlayerIds((responseData.ejectedPlayerIds || []).map(String));

    if (!options?.skipPossessionTimes) {
      const possessionTimes = responseData.possessionTimes;
      const preserveActiveTimer = options?.preserveActiveTimer !== false;
      const nextUsTime = Number(possessionTimes?.us || 0);
      const nextThemTime = Number(possessionTimes?.them || 0);

      if (!preserveActiveTimer || activeTimer !== 'US') {
        setUsTime(nextUsTime);
      }

      if (!preserveActiveTimer || activeTimer !== 'THEM') {
        setThemTime(nextThemTime);
      }
    }
  };

  const submitStatsEntries = async (
    entries: any[],
    options?: {
      quarter?: string;
      confirmPreviousPeriod?: boolean;
      filters?: StatsRefreshFilters;
      refreshOptions?: StatsRefreshOptions;
    },
  ): Promise<boolean> => {
    const quarterToUse = options?.quarter || activeQuarter;
    if (!quarterToUse) {
      return false;
    }

    const filters = options?.filters || {
      playerId: playerFilter || undefined,
      statType: statTypeFilter || undefined,
    };

    const payload = {
      entries: entries.map((entry) => {
        notePossessionOutcome(entry.player, entry.type);
        const eightMeterSideNow = eightMeterSideRef.current;
        const tagEightMeter = !!eightMeterSideNow && isEightMeterShotEntry(entry, eightMeterSideNow);
        return {
          ...entry,
          period: quarterToUse,
          ...(tagEightMeter ? { eightMeter: true, eightMeterSide: eightMeterSideNow } : {}),
        };
      }),
      updatestats: true,
      filters,
      includePossessionTimes: !options?.refreshOptions?.skipPossessionTimes,
      confirmPreviousPeriod: options?.confirmPreviousPeriod === true,
    };

    try {
      const response = await api.post(API_URLS.SUBMIT_STATS, payload);

      if (response.data?.requiresConfirmation) {
        setMostRecentQuarterWithData(response.data.quarterMismatch?.mostRecentPeriod || null);
        setPendingStatEntry({
          entries,
          quarter: quarterToUse,
          filters,
          refreshOptions: options?.refreshOptions,
        });
        setShowDataEntryWarning(true);
        return false;
      }

      if (response.data?.success) {
        const closedEightMeter = entries.some((entry) => EIGHT_METER_CLOSING_TYPES.includes(entry.type));
        if (closedEightMeter && eightMeterSideRef.current) {
          eightMeterSideRef.current = null;
          setEightMeterSide(null);
        }
        applyStatsRefreshPayload(response.data, options?.refreshOptions);
        return true;
      }

      showErrorMsg(response.data?.message || 'Failed to save stats data');
      return false;
    } catch (err) {
      console.error('Error submitting stats:', err);
      showErrorMsg('Failed to save stats data');
      throw err;
    }
  };

  // Utility function to submit stats and apply the returned refresh payload
  const addStatsAndRefresh = async (
    statsData: any,
    overrideQuarter?: string,
    refreshOptions?: StatsRefreshOptions,
  ): Promise<void> => {
    await enqueueStatMutation(async () => {
      await submitStatsEntries([statsData], {
        quarter: overrideQuarter,
        refreshOptions,
      });
    });
  };

  // Sends multiple stat entries in a single request then refreshes once
  const addStatsBatchAndRefresh = async (
    entries: any[],
    overrideQuarter?: string,
    refreshOptions?: StatsRefreshOptions,
  ): Promise<void> => {
    await enqueueStatMutation(async () => {
      await submitStatsEntries(entries, {
        quarter: overrideQuarter,
        refreshOptions,
      });
    });
  };

  const handleStatsFilterChange = (filters: { playerId: string; statType: string }) => {
    setPlayerFilter(filters.playerId);
    setStatTypeFilter(filters.statType);
    handleRefreshStats(filters);
  };

  const handleGoalClick = () => {
    setCurGoalStatus(true);
    setShowConfetti(true);
    setUsGoalClicked(true);

    setTimeout(() => {
      setUsGoalClicked(false);
      pendingIsTransitionRef.current = false;
      handleShotClick('US', 'goal');
    }, 500);

    setTimeout(() => {
      setShowConfetti(false);
      setCurGoalStatus(false);
    }, 8000);
  };

  const handleTheyGoalClick = () => {
    setThemGoalClicked(true);

    setTimeout(() => {
      setThemGoalClicked(false);
      pendingIsTransitionRef.current = false;
      handleShotClick('THEM', 'goal');
    }, 500);
  };

  const handleButtonClick = async (side: 'US' | 'THEM' | 'NONE') => {
    await runWithDirectActionLock(`possession-${side}`, async () => {
      setGlowButton(side);
      const now = Date.now();
      const prevActiveTimer = activeTimer;
      const prevUsLastStart = usLastStart;
      const prevThemLastStart = themLastStart;
      const prevUsTime = usTime;
      const prevThemTime = themTime;
      // Helper to call APIs
      const callPossessionAPIs = async (
        team: 'US' | 'THEM',
        lastStart: { value: number; timestamp: number } | null,
        currentValue: number,
      ) => {
        if (!lastStart || currentValue === 0) return;
        const justCountingValue = +(currentValue - lastStart.value).toFixed(3);
        if (justCountingValue <= 0) return;
        await addStatsAndRefresh(
          {
            period: activeQuarter,
            type: `Possession ${justCountingValue}`,
            player: team.toLowerCase(),
            clientId: clientId,
            seasonId: seasonId,
            gameId: gameId,
            outcome: consumePossessionOutcome(team.toLowerCase() as 'us' | 'them'),
          },
          undefined,
          // Don't re-fetch possession totals; it overwrites the running timer value.
          { skipPossessionTimes: true },
        );
      };

      if (side === 'US') {
        if (activeTimer === 'US') {
          // Consecutive US taps - just continue the existing possession, don't end and restart
          // Do nothing - let the timer continue running
        } else {
          // Switch immediately for UX (don't wait for network)
          setActiveTimer('US');
          setUsLastStart({ value: usTime, timestamp: now });
          setThemLastStart(null);

          if (prevActiveTimer === 'THEM') {
            await callPossessionAPIs('THEM', prevThemLastStart, prevThemTime);
          }
        }
      } else if (side === 'THEM') {
        if (activeTimer === 'THEM') {
          // Consecutive THEM taps - just continue the existing possession, don't end and restart
          // Do nothing - let the timer continue running
        } else {
          // Switch immediately for UX (don't wait for network)
          setActiveTimer('THEM');
          setThemLastStart({ value: themTime, timestamp: now });
          setUsLastStart(null);

          if (prevActiveTimer === 'US') {
            await callPossessionAPIs('US', prevUsLastStart, prevUsTime);
          }
        }
      } else {
        // NONE: stop whichever was running and log
        // Stop immediately for UX (don't wait for network)
        setActiveTimer(null);
        setUsLastStart(null);
        setThemLastStart(null);

        if (prevActiveTimer === 'US') {
          await callPossessionAPIs('US', prevUsLastStart, prevUsTime);
        } else if (prevActiveTimer === 'THEM') {
          await callPossessionAPIs('THEM', prevThemLastStart, prevThemTime);
        }

        // // Check for tied game when NONE is clicked in final regular period
        // const isFinalRegularPeriod = halfPeriod
        //   ? activeQuarter === "H2"
        //   : activeQuarter === "Q4";

        // if (isFinalRegularPeriod) {
        //   setTimeout(() => {
        //     checkForTiedGame();
        //   }, 500);
        // }
      }
      setTimeout(() => setGlowButton(null), 1000);
    });
  };

  // Penalty modal and card state
  const [showPenaltyModal, setShowPenaltyModal] = useState<null | 'US' | 'THEM'>(null);
  const [penaltySide, setPenaltySide] = useState<null | 'US' | 'THEM'>(null);
  const [cardPromptSide, setCardPromptSide] = useState<null | 'US' | 'THEM'>(null);
  const [pendingCardColor, setPendingCardColor] = useState<null | 'yellow' | 'red'>(null);
  const [usPenalty, setUsPenalty] = useState<any[]>([]);
  const [themPenalty, setThemPenalty] = useState<any[]>([]);
  const [selectedPenaltyPlayers, setSelectedPenaltyPlayers] = useState<number[]>([]);
  const [penaltySubmitting, setPenaltySubmitting] = useState(false);
  const penaltySubmittingRef = useRef(false);

  // New: THEM goalie modal state
  const [showThemGoalieModal, setShowThemGoalieModal] = useState(false);
  const [themGoalieType, setThemGoalieType] = useState<'goal' | 'saved' | null>(null);
  const [selectedThemGoalie, setSelectedThemGoalie] = useState<string | null>(null);

  // At game start (no stats yet), prompt to select the game goalie
  useEffect(() => {
    const maybePromptStartGoalie = async () => {
      if (goalieStartPromptCheckedRef.current) return;
      if (!gameGoalieLoaded || hideStatTakerButtons || !clientId || !seasonId || !gameId) return;
      if (!players.length) return;

      const goalies = players.filter(
        (p) => Array.isArray(p.position) && p.position.some((pos: string) => pos === 'Goalie'),
      );
      if (goalies.length === 0) {
        goalieStartPromptCheckedRef.current = true;
        return;
      }

      try {
        const response = await api.get(API_URLS.GET_MOST_RECENT_QUARTER, {
          params: { clientId, seasonId, gameId },
        });
        const hasStats = !!(response.data?.success && response.data.data);
        goalieStartPromptCheckedRef.current = true;

        if (!hasStats && !currentGoalieId) {
          const defaultGoalie =
            goalies.find((p) => String(p.playerId) === String(seasonDefaultGoalieId)) || null;
          setSelectedChangeGoalie(defaultGoalie ? (defaultGoalie.no as any) : null);
          setIsStartGoaliePrompt(true);
          setShowChangeGoalieModal(true);
        }
      } catch (err) {
        console.error('Failed to check game start goalie prompt:', err);
      }
    };

    maybePromptStartGoalie();
  }, [
    players,
    clientId,
    seasonId,
    gameId,
    currentGoalieId,
    seasonDefaultGoalieId,
    gameGoalieLoaded,
    hideStatTakerButtons,
  ]);

  // Selecting the carded player records the card and closes the window right away
  const handleSelectPenaltyPlayer = async (playerNo: number) => {
    if (penaltySubmittingRef.current) return;
    if (!penaltySide) return;

    penaltySubmittingRef.current = true;
    setPenaltySubmitting(true);
    setSelectedPenaltyPlayers([playerNo]);

    try {
      const player = players.find((p) => p.no === playerNo);
      if (penaltySide === 'US' && player) {
        await addStatsBatchAndRefresh([
          {
            period: activeQuarter,
            type: 'Penalty Start',
            cardColor: pendingCardColor,
            player: 'us',
            playerId: player._id,
            clientId: clientId,
            seasonId: seasonId,
            gameId: gameId,
          },
        ]);
      }

      // Close modal and reset selections
      setShowPenaltyModal(null);
      setPenaltySide(null);
      setSelectedPenaltyPlayers([]);
      setPendingCardColor(null);
    } catch (err) {
      console.error('Error creating card:', err);
      showErrorMsg('Failed to save card');
      setSelectedPenaltyPlayers([]);
    } finally {
      penaltySubmittingRef.current = false;
      setPenaltySubmitting(false);
    }
  };

  const getUsGoalies = () =>
    players.filter((p) => Array.isArray(p.position) && p.position.some((pos: string) => pos === 'Goalie'));

  const findGoalieBySeasonPlayerId = (seasonPlayerId: string | null) => {
    if (!seasonPlayerId) return null;
    return (
      players.find((p) => String(p.playerId) === String(seasonPlayerId)) ||
      players.find((p) => String(p._id) === String(seasonPlayerId)) ||
      null
    );
  };

  const resolveCurrentGoalie = () => findGoalieBySeasonPlayerId(currentGoalieId);

  const resolveDisplayGoalie = () =>
    resolveCurrentGoalie() || findGoalieBySeasonPlayerId(seasonDefaultGoalieId);

  const persistCurrentGoalie = async (seasonPlayerId: string | null) => {
    if (!gameId || !clientId) return;
    try {
      const response = await api.post(`${API_URLS.UPDATE_GAME_GOALIE}/${gameId}/goalie`, {
        clientId,
        currentGoalieId: seasonPlayerId,
      });
      if (response.data.success) {
        setCurrentGoalieId(response.data.data.currentGoalieId || null);
      }
    } catch (err) {
      console.error('Failed to persist current goalie:', err);
    }
  };

  const submitThemShotWithGoalie = async (
    type: 'goal' | 'saved',
    playerNo: number | string,
    isTransition = false,
  ) => {
    const goalie = players.find((p) => p.no === playerNo);
    if (!goalie) {
      console.error('Goalie not found:', playerNo);
      return;
    }

    const base = { clientId, seasonId, gameId };
    const locationFields = pickedLocationFields() || takeThemShotLocation();
    setShotLocationValue(null);
    setThemLocationDeferred(false);

    try {
      if (type === 'saved') {
        setActionFeedback('THEY_SHOT_SAVED');
      }

      if (type === 'goal') {
        await addStatsBatchAndRefresh([
          {
            ...base,
            type: 'Shot Goal',
            player: 'them',
            playerId: -1,
            isTransition: isTransition === true,
            ...locationFields,
          },
          {
            ...base,
            type: 'Shot not saved',
            player: 'us',
            playerId: goalie._id,
          },
        ]);
      } else {
        await addStatsBatchAndRefresh([
          {
            ...base,
            type: 'Shot Saved',
            player: 'them',
            playerId: -1,
            ...locationFields,
          },
          {
            ...base,
            type: 'Shot Was Saved By',
            player: 'us',
            playerId: goalie._id,
          },
        ]);
        setTimeout(() => setActionFeedback(null), 1000);
      }

      // Keep current goalie in sync with who was just used
      const seasonPlayerId = goalie.playerId ? String(goalie.playerId) : null;
      if (seasonPlayerId && seasonPlayerId !== String(currentGoalieId || '')) {
        setCurrentGoalieId(seasonPlayerId);
        await persistCurrentGoalie(seasonPlayerId);
      }
    } catch (err) {
      console.error('Error saving THEM shot with goalie:', err);
      showErrorMsg('Failed to save shot data');
      if (type === 'saved') {
        setActionFeedback(null);
      }
    }
  };

  const handleShotClick = async (side: 'US' | 'THEM', type: 'goal' | 'saved' | 'missed' | 'blocked') => {
    if (themLocationOpenRef.current) return;

    if (!isShotChartClient()) {
      if (side === 'THEM') {
        await proceedThemShot(type, false);
        return;
      }
      setShotModalSide(side);
      setShotModalType(type);
      setSelectedShotPlayer(null);
      setShotLocationValue(null);
      setShowShotModal(true);
      return;
    }

    // Blocked opponent shots stay on one screen: field on top, roster under it.
    if (side === 'THEM' && type === 'blocked') {
      themShotLocationRef.current = null;
      setShotLocationValue(null);
      setThemLocationDeferred(false);
      setShotModalSide('THEM');
      setShotModalType('blocked');
      setSelectedShotPlayer(null);
      setShowShotModal(true);
      return;
    }

    if (side === 'THEM') {
      themShotLocationRef.current = null;
      setShotLocationValue(null);
      setThemLocationDeferred(false);
      themLocationOpenRef.current = true;
      setThemLocationType(type);
      return;
    }

    // US shots always open modal for player selection (with the field picker on top)
    setShotModalSide(side);
    setShotModalType(type);
    setSelectedShotPlayer(null);
    setShotLocationValue(null);
    setShowShotModal(true);
  };

  const handleThemLocationDone = async (location: ShotLocation | null) => {
    const type = themLocationType;
    themLocationOpenRef.current = false;
    setThemLocationType(null);
    if (!type) return;
    themShotLocationRef.current = location;
    const deferred = location == null;
    setThemLocationDeferred(deferred);
    setShotLocationValue(null);
    await proceedThemShot(type, deferred);
  };

  const handleThemLocationCancel = () => {
    themLocationOpenRef.current = false;
    setThemLocationType(null);
    setThemLocationDeferred(false);
    setShotLocationValue(null);
    themShotLocationRef.current = null;
    pendingIsTransitionRef.current = false;
  };

  // Opponent shot outcome flow (goalie / blocker / missed). A location from the
  // zone sheet is waiting in themShotLocationRef. Skipping that sheet still
  // opens the next modal, and that modal shows the field.
  const proceedThemShot = async (type: 'goal' | 'saved' | 'missed' | 'blocked', deferred = false) => {
    if (type === 'goal' || type === 'saved') {
      const currentGoalie = deferred ? null : resolveCurrentGoalie();
      if (currentGoalie) {
        await runWithDirectActionLock(`shot-them-${type}`, async () => {
          await submitThemShotWithGoalie(type, currentGoalie.no, pendingIsTransitionRef.current);
          pendingIsTransitionRef.current = false;
        });
        return;
      }

      const goalies = getUsGoalies();
      if (!deferred && goalies.length === 1) {
        await runWithDirectActionLock(`shot-them-${type}`, async () => {
          await submitThemShotWithGoalie(type, goalies[0].no, pendingIsTransitionRef.current);
          pendingIsTransitionRef.current = false;
        });
        return;
      }

      setShowThemGoalieModal(true);
      setThemGoalieType(type);
      setSelectedThemGoalie(null);
      return;
    }

    // Blocked shots need player selection for who blocked
    if (type === 'blocked') {
      setShotModalSide('THEM');
      setShotModalType(type);
      setSelectedShotPlayer(null);
      setShowShotModal(true);
      return;
    }

    await runWithDirectActionLock('shot-them-missed', async () => {
      try {
        setActionFeedback('THEY_SHOT_MISSED');

        await addStatsAndRefresh({
          period: activeQuarter,
          type: 'Shot Missed',
          player: 'them',
          playerId: -1,
          clientId: clientId,
          seasonId: seasonId,
          gameId: gameId,
          ...takeThemShotLocation(),
        });
        setThemLocationDeferred(false);

        setTimeout(() => setActionFeedback(null), 1000);
      } catch (err) {
        console.error('Error saving THEM shot:', err);
        showErrorMsg('Failed to save shot data');
        setActionFeedback(null);
      }
    });
  };

  // Handler for THEM goalie modal selection
  const handleThemGoalieSelect = async (playerNo: number | string) => {
    await runWithSelectionSubmitLock(async () => {
      if (!themGoalieType) return;

      setSelectedThemGoalie(playerNo as any);
      await submitThemShotWithGoalie(themGoalieType, playerNo, pendingIsTransitionRef.current);
      pendingIsTransitionRef.current = false;

      setShowThemGoalieModal(false);
      setThemGoalieType(null);
      setSelectedThemGoalie(null);
    });
  };

  const handleChangeGoalieConfirm = async () => {
    if (selectedChangeGoalie === null || selectedChangeGoalie === '') {
      showErrorMsg('Please select a goalie');
      return;
    }

    await runWithSelectionSubmitLock(async () => {
      const goalie = players.find((p) => String(p.no) === String(selectedChangeGoalie));
      if (!goalie?.playerId) {
        showErrorMsg('Goalie not found');
        return;
      }

      try {
        const response = await api.post(`${API_URLS.UPDATE_GAME_GOALIE}/${gameId}/goalie`, {
          clientId,
          currentGoalieId: String(goalie.playerId),
        });
        if (response.data.success) {
          setCurrentGoalieId(response.data.data.currentGoalieId || String(goalie.playerId));
          showSuccessMsg('Goalie updated');
          setIsStartGoaliePrompt(false);
          setShowChangeGoalieModal(false);
          setSelectedChangeGoalie(null);
        } else {
          showErrorMsg(response.data.message || 'Failed to update goalie');
        }
      } catch (err: any) {
        showErrorMsg(err.response?.data?.message || 'Failed to update goalie');
      }
    });
  };

  const openGoaliePicker = (asStartPrompt = false) => {
    const defaultGoalie = findGoalieBySeasonPlayerId(currentGoalieId || seasonDefaultGoalieId);
    setSelectedChangeGoalie(defaultGoalie ? (defaultGoalie.no as any) : null);
    setIsStartGoaliePrompt(asStartPrompt);
    setShowChangeGoalieModal(true);
  };

  const handleShotPlayerSelect = async (playerNo: number | string) => {
    await runWithSelectionSubmitLock(async () => {
      setSelectedShotPlayer(playerNo as number);

      if (!shotModalSide || !shotModalType) return;

      const player = players.find((p) => p.no === playerNo);
      if (!player && shotModalSide === 'US') return;

      try {
        let statsType = '';
        switch (shotModalType) {
          case 'goal':
            statsType = 'Shot Goal';
            break;
          case 'saved':
            statsType = 'Shot Saved';
            break;
          case 'missed':
            statsType = 'Shot Missed';
            break;
          case 'blocked':
            statsType = 'Shot Blocked';
            break;
        }

        const locationFields = shotModalLocationFields();

        if (shotModalSide === 'THEM' && shotModalType === 'blocked') {
          const base = { clientId, seasonId, gameId };
          await addStatsBatchAndRefresh([
            {
              ...base,
              type: 'Shot Blocked',
              player: 'them',
              playerId: -1,
              ...locationFields,
            },
            {
              ...base,
              type: 'Was Blocked By',
              player: 'us',
              playerId: player._id,
            },
          ]);
        } else {
          await addStatsAndRefresh({
            period: activeQuarter,
            type: statsType,
            player: shotModalSide.toLowerCase(),
            playerId: shotModalSide === 'THEM' ? -1 : player._id,
            clientId: clientId,
            seasonId: seasonId,
            gameId: gameId,
            ...locationFields,
            ...(shotModalType === 'goal' && pendingIsTransitionRef.current ? { isTransition: true } : {}),
          });
          pendingIsTransitionRef.current = false;
        }

        setShowShotModal(false);
        setSelectedShotPlayer(null);
        setShotLocationValue(null);

        if (shotModalType === 'goal' && shotModalSide === 'US') {
          setGoalScorerForAssistPrompt(playerNo as number);
          setShowAssistPromptModal(true);
        } else {
          setShotModalSide(null);
          setShotModalType(null);
        }
      } catch (err) {
        console.error('Error saving shot:', err);
        showErrorMsg('Failed to save shot data');
        setShowShotModal(false);
        setShotModalSide(null);
        setShotModalType(null);
        setSelectedShotPlayer(null);
        setShotLocationValue(null);
      }
    });
  };

  const handleAssistPlayerSelect = async (playerNo: number | string) => {
    await runWithSelectionSubmitLock(async () => {
      setSelectedAssistPlayerForGoal(playerNo as number);

      const side = shotModalSide;
      const type = shotModalType;

      if (!side || !type) return;

      const assistPlayer = players.find((p) => p.no === playerNo);
      if (!assistPlayer) return;

      try {
        await addStatsAndRefresh({
          period: activeQuarter,
          type: 'Assist',
          player: 'us',
          playerId: assistPlayer._id,
          clientId: clientId,
          seasonId: seasonId,
          gameId: gameId,
        });

        setShowShotModal(false);
        setShowAssistModal(false);
        setShowAssistPromptModal(false);
        setShotModalSide(null);
        setShotModalType(null);
        setSelectedShotPlayer(null);
        setSelectedAssistPlayerForGoal(null);
        setGoalScorerForAssistPrompt(null);
      } catch (err) {
        console.error('Error saving assist:', err);
        showErrorMsg('Failed to save assist data');
      }
    });
  };

  // Wrapper function for assist modal selection
  const handleAssistModalSelect = (playerNo: number | string) => {
    handleAssistPlayerSelect(playerNo);
  };

  // Assist prompt handlers
  const handleAssistPromptResponse = (hasAssist: boolean) => {
    if (hasAssist) {
      // Show assist modal
      setShowAssistPromptModal(false);
      setShowAssistModal(true);
      setSelectedAssistPlayerForGoal(null);
    } else {
      // No assist - just close the prompt modal
      setShowAssistPromptModal(false);
      setGoalScorerForAssistPrompt(null);
    }
  };

  // Helper function to determine if Team Stat button should be shown
  const shouldShowTeamStatButton = (modalType: string, side: string, statType?: string): boolean => {
    // We stats that need Team Stat: Goal, Assist, Saved, Missed, Blocked, Ground Ball, CAUSED TURNOVER, Turnover, Penalty
    if (side === 'US') {
      if (modalType === 'shot') {
        return ['goal', 'saved', 'missed', 'blocked'].includes(statType || '');
      }
      if (modalType === 'assist') return true;
      if (modalType === 'turnover') {
        return ['onWeGroundBall', 'onWeStoleBall', 'onWeTurnover'].includes(statType || '');
      }
      if (modalType === 'penalty') return true;
    }

    // They stats that need Team Stat: Blocked, CAUSED TURNOVER
    if (side === 'THEM') {
      if (modalType === 'shot' && statType === 'blocked') return true;
      if (modalType === 'turnover' && statType === 'onTheyStoleBall') return true;
    }

    return false;
  };

  // Team stat handlers
  const handleTeamStatForShot = async () => {
    await runWithSelectionSubmitLock(async () => {
      if (!shotModalSide || !shotModalType) return;

      try {
        let statsType = '';
        switch (shotModalType) {
          case 'goal':
            statsType = 'Shot Goal';
            break;
          case 'saved':
            statsType = 'Shot Saved';
            break;
          case 'missed':
            statsType = 'Shot Missed';
            break;
          case 'blocked':
            statsType = 'Shot Blocked';
            break;
        }

        const locationFields = shotModalLocationFields();

        await addStatsAndRefresh({
          period: activeQuarter,
          type: statsType,
          player: shotModalSide === 'US' ? 'us' : 'them',
          playerId: -2,
          clientId: clientId,
          seasonId: seasonId,
          gameId: gameId,
          ...locationFields,
          ...(shotModalType === 'goal' && pendingIsTransitionRef.current ? { isTransition: true } : {}),
        });
        pendingIsTransitionRef.current = false;

        if (shotModalType === 'goal' && shotModalSide === 'US') {
          setShowConfetti(true);
          setTimeout(() => setShowConfetti(false), 10000);

          setGoalScorerForAssistPrompt('team');
          setShowAssistPromptModal(true);
        }
      } catch (err) {
        console.error('Error saving team shot stat:', err);
        showErrorMsg('Failed to save team shot data');
      }

      setShowShotModal(false);
      setShotModalSide(null);
      setShotModalType(null);
      setSelectedShotPlayer(null);
      setShotLocationValue(null);
    });
  };

  const handleTeamStatForAssist = async () => {
    await runWithSelectionSubmitLock(async () => {
      try {
        await addStatsAndRefresh({
          period: activeQuarter,
          type: 'Assist',
          player: 'us',
          playerId: -2,
          clientId: clientId,
          seasonId: seasonId,
          gameId: gameId,
        });
      } catch (err) {
        console.error('Error saving team assist stat:', err);
        showErrorMsg('Failed to save team assist data');
      }

      setShowAssistModal(false);
      setSelectedAssistPlayerForGoal(null);
      setShotModalSide(null);
      setShotModalType(null);
      setSelectedShotPlayer(null);
      setShowAssistPromptModal(false);
      setGoalScorerForAssistPrompt(null);
    });
  };

  const handleTeamStatForTurnover = async () => {
    await runWithSelectionSubmitLock(async () => {
      if (!turnoverModalType || !turnoverModalSide) return;

      try {
        let statsType = '';

        if (turnoverModalType === 'onWeGroundBall') {
          statsType = 'Ground Ball';
        } else if (turnoverModalType === 'onWeStoleBall') {
          await addStatsBatchAndRefresh([
            {
              period: activeQuarter,
              type: 'Caused Turnover',
              player: 'us',
              playerId: -2,
              clientId: clientId,
              seasonId: seasonId,
              gameId: gameId,
            },
            {
              period: activeQuarter,
              type: 'Turnover',
              player: 'them',
              playerId: -1,
              clientId: clientId,
              seasonId: seasonId,
              gameId: gameId,
            },
          ]);
        } else if (turnoverModalType === 'onWeTurnover') {
          statsType = 'Turnover';
        } else if (turnoverModalType === 'onTheyStoleBall') {
          await addStatsBatchAndRefresh([
            {
              period: activeQuarter,
              type: 'Forced Turnover',
              player: 'them',
              playerId: -2,
              clientId: clientId,
              seasonId: seasonId,
              gameId: gameId,
            },
            {
              period: activeQuarter,
              type: 'Turnover',
              player: 'us',
              playerId: -2,
              clientId: clientId,
              seasonId: seasonId,
              gameId: gameId,
            },
          ]);
        }

        if (statsType && (turnoverModalType === 'onWeGroundBall' || turnoverModalType === 'onWeTurnover')) {
          await addStatsAndRefresh({
            period: activeQuarter,
            type: statsType,
            player: 'us',
            playerId: -2,
            clientId: clientId,
            seasonId: seasonId,
            gameId: gameId,
          });
        }
      } catch (err) {
        console.error('Error saving team turnover stat:', err);
        showErrorMsg('Failed to save team turnover data');
      }

      setShowTurnoverModal(false);
      setTurnoverModalType(null);
      setTurnoverModalSide(null);
      setSelectedTurnoverPlayer(null);
    });
  };

  const handleTeamStatForPenalty = async () => {
    if (penaltySubmittingRef.current) return;
    penaltySubmittingRef.current = true;
    setPenaltySubmitting(true);
    try {
      await addStatsAndRefresh({
        period: activeQuarter,
        type: 'Penalty Start',
        cardColor: pendingCardColor,
        player: 'us',
        playerId: -2, // Team stat
        clientId: clientId,
        seasonId: seasonId,
        gameId: gameId,
      });
    } catch (err) {
      console.error('Error creating team penalty:', err);
      showErrorMsg('Failed to create team penalty');
    } finally {
      // Close modal
      setShowPenaltyModal(null);
      setPenaltySide(null);
      setSelectedPenaltyPlayers([]);
      setPendingCardColor(null);
      penaltySubmittingRef.current = false;
      setPenaltySubmitting(false);
    }
  };

  // Yellow/Red card: ask the color, then attribute it.
  const handlePenaltyClick = (side: 'US' | 'THEM') => {
    if (penaltySubmittingRef.current) return;
    setCardPromptSide(side);
  };

  const handleCardColorSelect = async (color: 'yellow' | 'red') => {
    const side = cardPromptSide;
    setCardPromptSide(null);
    if (!side || penaltySubmittingRef.current) return;
    setPendingCardColor(color);

    if (side === 'US') {
      setShowPenaltyModal(side);
      setPenaltySide(side);
      setSelectedPenaltyPlayers([]);
      return;
    }

    await runWithDirectActionLock('penalty-add-THEM', async () => {
      try {
        penaltySubmittingRef.current = true;
        setPenaltySubmitting(true);
        await addStatsAndRefresh({
          period: activeQuarter,
          type: 'Penalty Start',
          cardColor: color,
          player: 'them',
          playerId: -1,
          clientId: clientId,
          seasonId: seasonId,
          gameId: gameId,
        });
      } catch (err) {
        console.error('Error creating THEM card:', err);
        showErrorMsg('Failed to save card');
      } finally {
        penaltySubmittingRef.current = false;
        setPenaltySubmitting(false);
        setPendingCardColor(null);
      }
    });
  };

  const resetEightMeterFlow = () => {
    setEightMeterFlowSide(null);
    setShowEightMeterFoulerModal(false);
    setShowEightMeterFoulModal(false);
    setEightMeterFoulerId(null);
  };

  const handleEightMeter = (side: 'US' | 'THEM') => {
    setEightMeterFlowSide(side);
    setEightMeterFoulerId(null);
    if (side === 'THEM') {
      setShowEightMeterFoulerModal(true);
    } else {
      setShowEightMeterFoulModal(true);
    }
  };

  const handleEightMeterFoulerSelect = (playerNo: number | string) => {
    const player = players.find((p) => p.no === playerNo);
    if (!player) return;
    setEightMeterFoulerId(player._id);
    setShowEightMeterFoulerModal(false);
    setShowEightMeterFoulModal(true);
  };

  const handleEightMeterFoulerTeamStat = () => {
    setEightMeterFoulerId(-2);
    setShowEightMeterFoulerModal(false);
    setShowEightMeterFoulModal(true);
  };

  const handleEightMeterFoulConfirm = async (fouls: EightMeterFoul[]) => {
    const side = eightMeterFlowSide;
    if (!side || fouls.length === 0) return;

    await runWithDirectActionLock(`eight-meter-${side}`, async () => {
      try {
        await addStatsAndRefresh({
          period: activeQuarter,
          type: '8M Free Possession',
          player: side.toLowerCase(),
          // For THEM, the 8M log carries our player who committed the foul
          playerId: side === 'THEM' ? eightMeterFoulerId ?? -2 : -1,
          fouls,
          clientId: clientId,
          seasonId: seasonId,
          gameId: gameId,
        });
        eightMeterSideRef.current = side;
        setEightMeterSide(side);
      } catch (err) {
        console.error('Error saving 8 meter free possession:', err);
        showErrorMsg('Failed to save 8 meter free possession');
      } finally {
        resetEightMeterFlow();
      }
    });
  };

  // Remove penalty card (DELETE: remove from database and state)
  const handleDeletePenalty = async (side: 'US' | 'THEM', id: string) => {
    const penalties = side === 'US' ? usPenalty : themPenalty;
    const penalty = penalties.find((p) => p.id === id);

    if (!penalty) return;

    await runWithDirectActionLock(`penalty-delete-${side}-${id}`, async () => {
      try {
        await api.delete(API_URLS.DELETE_STATS, {
          data: { _id: penalty.statId || penalty.id },
        });

        await handleRefreshStats();
      } catch (err) {
        console.error('Error deleting penalty stat:', err);
        showErrorMsg('Failed to delete penalty');
      }
    });
  };

  // End penalty card (END: call API, then refresh stats)
  const handleEndPenalty = async (side: 'US' | 'THEM', id: string) => {
    const penalties = side === 'US' ? usPenalty : themPenalty;
    const penalty = penalties.find((p) => p.id === id);
    if (!penalty) return;

    await runWithDirectActionLock(`penalty-end-${side}-${id}`, async () => {
      try {
        const playerId = side === 'THEM' ? -1 : (penalty.player?._id ?? penalty.playerId ?? penalty.id);

        await addStatsAndRefresh({
          period: activeQuarter,
          type: 'Penalty End',
          player: side.toLowerCase(),
          playerId: playerId,
          penaltyStartId: penalty.statId || penalty.id,
          cardColor: penalty.cardColor,
          clientId: clientId,
          seasonId: seasonId,
          gameId: gameId,
        });
      } catch (err) {
        console.error('Error ending penalty:', err);
        showErrorMsg('Failed to end penalty');
      }
    });
  };

  // Turnover modal state
  const [showTurnoverModal, setShowTurnoverModal] = useState(false);
  const [turnoverModalType, setTurnoverModalType] = useState<
    null | 'onWeGroundBall' | 'onWeStoleBall' | 'onWeTurnover' | 'onTheyStoleBall'
  >(null);
  const [turnoverModalSide, setTurnoverModalSide] = useState<'US' | 'THEM' | null>(null);
  const [selectedTurnoverPlayer, setSelectedTurnoverPlayer] = useState<number | string | null>(null);

  // Shot Clock Violation modal state
  const [showShotClockModal, setShowShotClockModal] = useState(false);
  const [shotClockEnabled, setShotClockEnabled] = useState(false); // Will be set based on season data

  // Handler for US Shot Clock Violation (direct action, no modal needed)
  const handleWeShotClockViolation = async () => {
    await runWithDirectActionLock('shot-clock-us', async () => {
      try {
        setActionFeedback('WE_SHOT_CLOCK');

        await addStatsAndRefresh({
          period: activeQuarter,
          type: 'Shot Clock Violation',
          player: 'us',
          playerId: -1,
          clientId: clientId,
          seasonId: seasonId,
          gameId: gameId,
        });

        setTimeout(() => setActionFeedback(null), 1000);
      } catch (err) {
        showErrorMsg('Failed to save shot clock violation data');
        setActionFeedback(null);
      }
    });
  };

  // Handler for THEM Shot Clock Violation YES
  const handleTheyShotClockViolationYes = async () => {
    await runWithDirectActionLock('shot-clock-them-yes', async () => {
      try {
        await addStatsAndRefresh({
          period: activeQuarter,
          type: 'Shot Clock Violation',
          player: 'them',
          playerId: -1,
          clientId: clientId,
          seasonId: seasonId,
          gameId: gameId,
        });
      } catch (err) {
        showErrorMsg('Failed to save shot clock violation data');
      }
    });
    setShowShotClockModal(false);
  };

  // Handler for THEM Shot Clock Violation NO (regular turnover)
  const handleTheyShotClockViolationNo = async () => {
    await runWithDirectActionLock('shot-clock-them-no', async () => {
      await addStatsAndRefresh({
        period: activeQuarter,
        type: 'Turnover',
        player: 'them',
        playerId: -1,
        clientId: clientId,
        seasonId: seasonId,
        gameId: gameId,
      });
    });

    setShowShotClockModal(false);
  };

  // Handler for US turnover actions (open modal)
  const handleWeTurnoverAction = (type: 'onWeGroundBall' | 'onWeStoleBall' | 'onWeTurnover') => {
    setShowTurnoverModal(true);
    setTurnoverModalType(type);
    setTurnoverModalSide('US');
    setSelectedTurnoverPlayer(null);
  };

  // Handler for THEM CAUSED TURNOVER (open modal)
  const handleTheyStoleBall = () => {
    setShowTurnoverModal(true);
    setTurnoverModalType('onTheyStoleBall');
    setTurnoverModalSide('THEM');
    setSelectedTurnoverPlayer(null);
  };

  // Handler for US turnover modal confirm
  const handleTurnoverPlayerSelect = async (playerNo: number | string) => {
    await runWithSelectionSubmitLock(async () => {
      setSelectedTurnoverPlayer(playerNo);

      if (typeof playerNo === 'string' && playerNo === 'THEM') {
        let statsType = '';
        if (turnoverModalType === 'onWeStoleBall') {
          statsType = 'Caused Turnover';
        } else if (turnoverModalType === 'onTheyStoleBall') {
          statsType = 'Forced Turnover';
        }

        try {
          await addStatsAndRefresh({
            period: activeQuarter,
            type: statsType,
            player: 'them',
            playerId: -1,
            clientId: clientId,
            seasonId: seasonId,
            gameId: gameId,
          });
        } catch (err) {
          showErrorMsg('Failed to save turnover data');
        }
        setShowTurnoverModal(false);
        setTurnoverModalType(null);
        setTurnoverModalSide(null);
        setSelectedTurnoverPlayer(null);
        return;
      }

      const player = players.find((p) => p.no === playerNo);
      if (!player) return;

      let statsType = '';
      if (turnoverModalType === 'onWeGroundBall') {
        statsType = 'Ground Ball';
      } else if (turnoverModalType === 'onWeStoleBall') {
        try {
          await addStatsBatchAndRefresh([
            {
              period: activeQuarter,
              type: 'Caused Turnover',
              player: 'us',
              playerId: player._id,
              clientId: clientId,
              seasonId: seasonId,
              gameId: gameId,
            },
            {
              period: activeQuarter,
              type: 'Turnover',
              player: 'them',
              playerId: -1,
              clientId: clientId,
              seasonId: seasonId,
              gameId: gameId,
            },
          ]);
        } catch (err) {
          showErrorMsg('Failed to save turnover data');
        }
        setShowTurnoverModal(false);
        setTurnoverModalType(null);
        setTurnoverModalSide(null);
        setSelectedTurnoverPlayer(null);
        return;
      } else if (turnoverModalType === 'onWeTurnover') {
        statsType = 'Turnover';
      } else if (turnoverModalType === 'onTheyStoleBall') {
        try {
          await addStatsBatchAndRefresh([
            {
              period: activeQuarter,
              type: 'Turnover',
              player: 'us',
              playerId: player._id,
              clientId: clientId,
              seasonId: seasonId,
              gameId: gameId,
            },
            {
              period: activeQuarter,
              type: 'Caused Turnover',
              player: 'them',
              playerId: -1,
              clientId: clientId,
              seasonId: seasonId,
              gameId: gameId,
            },
          ]);
        } catch (err) {
          showErrorMsg('Failed to save turnover data');
        }
        setShowTurnoverModal(false);
        setTurnoverModalType(null);
        setTurnoverModalSide(null);
        setSelectedTurnoverPlayer(null);
        return;
      }

      try {
        await addStatsAndRefresh({
          period: activeQuarter,
          type: statsType,
          player: turnoverModalSide === 'US' ? 'us' : 'them',
          playerId: player._id,
          clientId: clientId,
          seasonId: seasonId,
          gameId: gameId,
        });
      } catch (err) {
        showErrorMsg('Failed to save turnover data');
      }

      setShowTurnoverModal(false);
      setTurnoverModalType(null);
      setTurnoverModalSide(null);
      setSelectedTurnoverPlayer(null);
    });
  };

  // Handler for THEM ground ball and turnover (no modal)
  const handleTheyGroundBallOrTurnover = async (type: 'Ground Ball' | 'Turnover', possessiontype: string) => {
    await runWithDirectActionLock(`them-${type.toLowerCase().replace(/\s+/g, '-')}`, async () => {
      try {
        setActionFeedback(type === 'Ground Ball' ? 'THEM_GROUND_BALL' : 'THEM_TURNOVER');

        await addStatsAndRefresh({
          period: activeQuarter,
          type,
          player: 'them',
          playerId: -1,
          clientId: clientId,
          seasonId: seasonId,
          gameId: gameId,
        });

        setTimeout(() => setActionFeedback(null), 1000);
      } catch (err) {
        showErrorMsg('Failed to save turnover data');
        setActionFeedback(null);
      }
    });
  };

  // Handler for THEM turnover button - checks shot clock setting
  const handleTheyTurnover = () => {
    if (shotClockEnabled) {
      // Show shot clock violation Y/N modal
      setShowShotClockModal(true);
    } else {
      // Record regular turnover directly
      handleTheyGroundBallOrTurnover('Turnover', 'turnover');
    }
  };

  // Handler for WE Clear Success
  const handleWeClearSuccess = async () => {
    await runWithDirectActionLock('clear-us-success', async () => {
      try {
        setActionFeedback('WE_CLEAR_SUCCESS');

        await addStatsAndRefresh({
          period: activeQuarter,
          type: 'Clear Success',
          player: 'us',
          playerId: 'us',
          clientId: clientId,
          seasonId: seasonId,
          gameId: gameId,
        });

        setTimeout(() => setActionFeedback(null), 1000);
      } catch (err) {
        showErrorMsg('Failed to save clear data');
        setActionFeedback(null);
      }
    });
  };

  // Handler for WE Clear Fail
  const handleWeClearFail = async () => {
    await runWithDirectActionLock('clear-us-fail', async () => {
      try {
        setActionFeedback('WE_CLEAR_FAIL');

        await addStatsAndRefresh({
          period: activeQuarter,
          type: 'Clear Fail',
          player: 'us',
          playerId: 'us',
          clientId: clientId,
          seasonId: seasonId,
          gameId: gameId,
        });

        setTimeout(() => setActionFeedback(null), 1000);
      } catch (err) {
        showErrorMsg('Failed to save clear data');
        setActionFeedback(null);
      }
    });
  };

  // Handler for THEY Clear Success
  const handleTheyClearSuccess = async () => {
    await runWithDirectActionLock('clear-them-success', async () => {
      try {
        setActionFeedback('THEY_CLEAR_SUCCESS');

        await addStatsAndRefresh({
          period: activeQuarter,
          type: 'Clear Success',
          player: 'them',
          playerId: -1,
          clientId: clientId,
          seasonId: seasonId,
          gameId: gameId,
        });
        setTimeout(() => setActionFeedback(null), 1000);
      } catch (err) {
        showErrorMsg('Failed to save clear data');
        setActionFeedback(null);
      }
    });
  };

  // Handler for THEY Clear Fail
  const handleTheyClearFail = async () => {
    await runWithDirectActionLock('clear-them-fail', async () => {
      try {
        setActionFeedback('THEY_CLEAR_FAIL');

        await addStatsAndRefresh({
          period: activeQuarter,
          type: 'Clear Fail',
          player: 'them',
          playerId: -1,
          clientId: clientId,
          seasonId: seasonId,
          gameId: gameId,
        });

        setTimeout(() => setActionFeedback(null), 1000);
      } catch (err) {
        showErrorMsg('Failed to save clear data');
        setActionFeedback(null);
      }
    });
  };

  // The draw taker is picked ahead of the draw and stays selected until changed
  const [drawTakerId, setDrawTakerId] = useState<string>('');
  const [showFaceoffModal, setShowFaceoffModal] = useState(false);
  const [showFaceoffFullRosterModal, setShowFaceoffFullRosterModal] = useState(false);
  const [selectedFaceoffPlayer, setSelectedFaceoffPlayer] = useState<number | string | null>(null);

  const drawTakerPlayerId = () => (players.some((p) => String(p._id) === drawTakerId) ? drawTakerId : -2);

  const openDrawControlWon = () => {
    setSelectedFaceoffPlayer(null);
    setShowFaceoffModal(true);
  };

  const closeDrawControl = () => {
    setShowFaceoffModal(false);
    setShowFaceoffFullRosterModal(false);
    setSelectedFaceoffPlayer(null);
  };

  const handleDrawControlEarned = async (playerNo: number | string) => {
    const earner = players.find((p) => p.no === playerNo);
    if (!earner) {
      console.error('Player not found:', playerNo);
      return;
    }

    await runWithSelectionSubmitLock(async () => {
      try {
        const base = { player: 'us', clientId, seasonId, gameId };
        // NCAA: earning the draw control does not also award a ground ball
        await addStatsBatchAndRefresh([
          { ...base, type: 'Faceoff Won', playerId: drawTakerPlayerId() },
          { ...base, type: 'Draw Control Earned', playerId: earner._id },
        ]);
      } catch (err) {
        showErrorMsg('Failed to save draw control data');
      }
    });
    closeDrawControl();
  };

  const handleDrawControlLost = async () => {
    await runWithDirectActionLock('draw-control-them', async () => {
      try {
        setActionFeedback('THEM_DRAW_CONTROL');
        await addStatsBatchAndRefresh([
          { type: 'Faceoff Won', player: 'them', playerId: -1, clientId, seasonId, gameId },
          { type: 'Faceoff Lost', player: 'us', playerId: drawTakerPlayerId(), clientId, seasonId, gameId },
        ]);
        setTimeout(() => setActionFeedback(null), 1000);
      } catch (err) {
        showErrorMsg('Failed to save draw control data');
        setActionFeedback(null);
      }
    });
  };

  // Add state for AddStatModal
  const [showAddStatModal, setShowAddStatModal] = useState(false);

  const [playerFilter, setPlayerFilter] = useState('');
  const [statTypeFilter, setStatTypeFilter] = useState('');

  // Add state for tied game detection and OT prompt
  // const [showTiedGamePrompt, setShowTiedGamePrompt] = useState(false);
  // const [gameScores, setGameScores] = useState({ us: 0, them: 0 });
  // const [hasCheckedTiedGame, setHasCheckedTiedGame] = useState(false);

  // Data entry warning state
  const [showDataEntryWarning, setShowDataEntryWarning] = useState(false);
  const [mostRecentQuarterWithData, setMostRecentQuarterWithData] = useState<string | null>(null);
  const [pendingStatEntry, setPendingStatEntry] = useState<PendingStatSubmission | null>(null);

  // Custom setter for activeQuarter that persists to sessionManager
  const updateActiveQuarter = (quarter: string) => {
    setActiveQuarter(quarter);
    sessionManager.setActiveQuarter(quarter);
  };

  // Handler for data entry warning modal - proceed in current quarter
  const handleDataEntryProceed = async () => {
    setShowDataEntryWarning(false);
    if (pendingStatEntry) {
      setPendingStatEntry(null);
      await enqueueStatMutation(async () => {
        await submitStatsEntries(pendingStatEntry.entries, {
          quarter: pendingStatEntry.quarter,
          confirmPreviousPeriod: true,
          filters: pendingStatEntry.filters,
          refreshOptions: pendingStatEntry.refreshOptions,
        });
      });
    }
  };

  // Handler for data entry warning modal - switch to most recent quarter and then execute the stat entry
  const handleDataEntrySwitchQuarter = async () => {
    if (mostRecentQuarterWithData && pendingStatEntry) {
      const targetQuarter = mostRecentQuarterWithData;
      const pendingEntry = pendingStatEntry;
      setPendingStatEntry(null);

      // First, switch to the most recent quarter
      updateActiveQuarter(targetQuarter);

      // Close the warning modal
      setShowDataEntryWarning(false);

      // Execute the pending stat entry in the new quarter
      setTimeout(async () => {
        await enqueueStatMutation(async () => {
          await submitStatsEntries(pendingEntry.entries, {
            quarter: targetQuarter,
            confirmPreviousPeriod: true,
            filters: pendingEntry.filters,
            refreshOptions: pendingEntry.refreshOptions,
          });
        });
      }, 100);
    } else {
      // If no most recent quarter, just close and discard
      setShowDataEntryWarning(false);
      setPendingStatEntry(null);
    }
  };

  // Handler for Add Stat button
  const handleAddStatClick = () => {
    setShowAddStatModal(true);
  };

  if (!activeQuarter) {
    return (
      <div className="flex justify-center items-center py-8">
        <div role="status">
          <svg
            aria-hidden="true"
            className="w-8 h-8 text-gray-200 animate-spin dark:text-gray-600 fill-blue-600"
            viewBox="0 0 100 101"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M100 50.5908C100 78.2051 77.6142 100.591 50 100.591C22.3858 100.591 0 78.2051 0 50.5908C0 22.9766 22.3858 0.59082 50 0.59082C77.6142 0.59082 100 22.9766 100 50.5908ZM9.08144 50.5908C9.08144 73.1895 27.4013 91.5094 50 91.5094C72.5987 91.5094 90.9186 73.1895 90.9186 50.5908C90.9186 27.9921 72.5987 9.67226 50 9.67226C27.4013 9.67226 9.08144 27.9921 9.08144 50.5908Z"
              fill="currentColor"
            />
            <path
              d="M93.9676 39.0409C96.393 38.4038 97.8624 35.9116 97.0079 33.5539C95.2932 28.8227 92.871 24.3692 89.8167 20.348C85.8452 15.1192 80.8826 10.7238 75.2124 7.41289C69.5422 4.10194 63.2754 1.94025 56.7698 1.05124C51.7666 0.367541 46.6976 0.446843 41.7345 1.27873C39.2613 1.69328 37.813 4.19778 38.4501 6.62326C39.0873 9.04874 41.5694 10.4717 44.0505 10.1071C47.8511 9.54855 51.7191 9.52689 55.5402 10.0491C60.8642 10.7766 65.9928 12.5457 70.6331 15.2552C75.2735 17.9648 79.3347 21.5619 82.5849 25.841C84.9175 28.9121 86.7997 32.2913 88.1811 35.8758C89.083 38.2158 91.5421 39.6781 93.9676 39.0409Z"
              fill="currentFill"
            />
          </svg>
          <span className="sr-only">Loading...</span>
        </div>
      </div>
    );
  }

  const displayGoaliePlayer = resolveDisplayGoalie();
  const currentGoalieLabel = displayGoaliePlayer
    ? `#${displayGoaliePlayer.no} ${displayGoaliePlayer.firstName || ''} ${displayGoaliePlayer.lastName || ''}`.trim()
    : 'Not set';

  const ejectedIdSet = new Set(ejectedPlayerIds);
  const capturePlayers = players.filter((p) => !ejectedIdSet.has(String(p._id)));
  const midfieldPlayers = capturePlayers.filter(
    (p) => Array.isArray(p.position) && p.position.some((pos: string) => pos === 'Midfield'),
  );
  return (
    <div className="w-full">
      {/* Quarter Selector */}
      <QuarterSelector periods={periods} activeQuarter={activeQuarter} onSelectQuarter={updateActiveQuarter} />

      {!hideStatTakerButtons && gameTrackingData.shots && (
        <div className="mx-3 mt-3 mb-1 flex items-center justify-between gap-3 rounded-lg border border-sd-line bg-sd-light px-3 py-2">
          <div className="text-sm sm:text-base font-semibold text-sd-navy">
            Goalie: <span className="font-bold">{currentGoalieLabel}</span>
            {!currentGoalieId && seasonDefaultGoalieId && displayGoaliePlayer ? (
              <span className="ml-1 font-normal text-gray-600">(season default)</span>
            ) : null}
          </div>
          <button
            type="button"
            className="shrink-0 rounded-md bg-sd-navy px-3 py-1.5 text-sm font-semibold text-white"
            onClick={() => openGoaliePicker(false)}
          >
            Change
          </button>
        </div>
      )}

      {loading && (
        <div className="flex justify-center items-center py-8">
          <div role="status">
            <svg
              aria-hidden="true"
              className="w-8 h-8 text-gray-200 animate-spin dark:text-gray-600 fill-blue-600"
              viewBox="0 0 100 101"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M100 50.5908C100 78.2051 77.6142 100.591 50 100.591C22.3858 100.591 0 78.2051 0 50.5908C0 22.9766 22.3858 0.59082 50 0.59082C77.6142 0.59082 100 22.9766 100 50.5908ZM9.08144 50.5908C9.08144 73.1895 27.4013 91.5094 50 91.5094C72.5987 91.5094 90.9186 73.1895 90.9186 50.5908C90.9186 27.9921 72.5987 9.67226 50 9.67226C27.4013 9.67226 9.08144 27.9921 9.08144 50.5908Z"
                fill="currentColor"
              />
              <path
                d="M93.9676 39.0409C96.393 38.4038 97.8624 35.9116 97.0079 33.5539C95.2932 28.8227 92.871 24.3692 89.8167 20.348C85.8452 15.1192 80.8826 10.7238 75.2124 7.41289C69.5422 4.10194 63.2754 1.94025 56.7698 1.05124C51.7666 0.367541 46.6976 0.446843 41.7345 1.27873C39.2613 1.69328 37.813 4.19778 38.4501 6.62326C39.0873 9.04874 41.5694 10.4717 44.0505 10.1071C47.8511 9.54855 51.7191 9.52689 55.5402 10.0491C60.8642 10.7766 65.9928 12.5457 70.6331 15.2552C75.2735 17.9648 79.3347 21.5619 82.5849 25.841C84.9175 28.9121 86.7997 32.2913 88.1811 35.8758C89.083 38.2158 91.5421 39.6781 93.9676 39.0409Z"
                fill="currentFill"
              />
            </svg>
            <span className="sr-only">Loading...</span>
          </div>
        </div>
      )}
      {hideStatTakerButtons && (
        <div className="flex justify-center items-center py-8">
          <div className="text-center">
            <div className="text-lg font-semibold text-gray-700 mb-2">🔒 Game Locked</div>
            <div className="text-sm text-gray-600">
              Stats are locked for games that started more than 24 hours ago.
              <br />
              Contact your Head Coach if you need to make changes.
            </div>
          </div>
        </div>
      )}
      {!loading && !hideStatTakerButtons && (
          <>
            {(isTouchOnly || width < 1200) && (
              // On desktop the field keeps the phone/tablet proportions instead of stretching to the window
              <div className="relative mt-4 w-full overflow-hidden border-y border-sd-line bg-sd-light lg:mx-auto lg:max-w-[720px] lg:border-x">
                <CapturePlayground />

                <div className="relative z-10 flex flex-col gap-8 pt-[17%] pb-[17%]">
                  {/* 8M Free Possession and Yellow / Red Card share one tracking role */}
                  {gameTrackingData.penalties && (
                    <>
                      <EightMeterSection
                        onUs={() => handleEightMeter('US')}
                        onThem={() => handleEightMeter('THEM')}
                        disabled={!!directActionLocks['eight-meter-US'] || !!directActionLocks['eight-meter-THEM']}
                      />
                      {eightMeterSide && (
                        <div className="mx-3 rounded-lg bg-[#f5b942] text-sd-ink px-4 py-3 flex items-center justify-between gap-3 shadow-lg">
                          <div>
                            <div className="text-xs font-bold tracking-wide">8 METER FREE POSSESSION</div>
                            <div className="text-xl font-extrabold">{eightMeterSide}</div>
                            <div className="text-xs font-semibold">Shots in this possession count as 8M</div>
                          </div>
                          <button
                            type="button"
                            className="shrink-0 rounded-md bg-sd-ink px-3 py-1.5 text-sm font-semibold text-white"
                            onClick={() => {
                              eightMeterSideRef.current = null;
                              setEightMeterSide(null);
                            }}
                          >
                            End
                          </button>
                        </div>
                      )}
                      {usPenalty.length > themPenalty.length && (
                        <div className="mx-3 bg-[#b71c1c] text-white rounded-lg shadow-lg px-4 py-3 flex flex-col border-2 relative animate-fade-in">
                          <div className="text-2xl md:text-3xl font-extrabold tracking-wide mb-2">MAN DOWN</div>
                          <div className="text-left w-full max-w-lg space-y-1">
                            <div>
                              <span className="font-bold">They score:</span>
                              <span className="ml-1">Man Down Defense Failure (MDDF)</span>
                            </div>
                            <div>
                              <span className="font-bold">No score:</span>
                              <span className="ml-1">Man Down Defense Success (MDDS)</span>
                            </div>
                          </div>
                        </div>
                      )}
                      {themPenalty.length > usPenalty.length && (
                        <div className="mx-3 bg-[#4caf50] text-white rounded-lg shadow-lg px-4 py-3 flex flex-col border-2 relative animate-fade-in">
                          <div className="text-2xl md:text-3xl font-extrabold tracking-wide mb-2">MAN UP</div>
                          <div className="text-left w-full max-w-lg space-y-1">
                            <div>
                              <span className="font-bold">We score:</span>
                              <span className="ml-1">Man Up Offense Success (MUOS)</span>
                            </div>
                            <div>
                              <span className="font-bold">Otherwise:</span>
                              <span className="ml-1">Man Up Offense Failure (MUOF)</span>
                            </div>
                          </div>
                        </div>
                      )}
                      <PenaltiesSection
                        redCardMinutes={RED_CARD_MINUTES}
                        usPenalties={usPenalty}
                        themPenalties={themPenalty}
                        onDeletePenalty={handleDeletePenalty}
                        onEndPenalty={handleEndPenalty}
                        onAddPenalty={handlePenaltyClick}
                        submittingActionKey={
                          Object.keys(directActionLocks).find((key) => key.startsWith('penalty-')) || null
                        }
                      />
                    </>
                  )}

                  <div className="z-10">
                    {/* Possession Times */}
                    {gameTrackingData.possessionTime && (
                      <PossessionTimesSection
                        usTime={usTime}
                        themTime={themTime}
                        activeTimer={activeTimer}
                        glowButton={glowButton}
                        formatTime={formatTime}
                        onButtonClick={handleButtonClick}
                        disabledButtons={{
                          US: !!directActionLocks['possession-US'],
                          THEM: !!directActionLocks['possession-THEM'],
                          NONE: !!directActionLocks['possession-NONE'],
                        }}
                      />
                    )}

                    {/* Shots */}
                    {gameTrackingData.shots && (
                      <ShotsSection
                        hoveredTheyShot={hoveredTheyShot}
                        curGoalStatus={curGoalStatus}
                        usGoalClicked={usGoalClicked}
                        themGoalClicked={themGoalClicked}
                        onGoalClick={handleGoalClick}
                        onTheyGoalClick={handleTheyGoalClick}
                        onUsSavedClick={() => handleShotClick('US', 'saved')}
                        onUsMissedClick={() => handleShotClick('US', 'missed')}
                        onUsBlockedClick={() => handleShotClick('US', 'blocked')}
                        onTheySavedClick={() => handleShotClick('THEM', 'saved')}
                        onTheyMissedClick={() => handleShotClick('THEM', 'missed')}
                        onTheyBlockedClick={() => handleShotClick('THEM', 'blocked')}
                        setHoveredTheyShot={setHoveredTheyShot}
                        actionFeedback={actionFeedback}
                        theyMissedDisabled={!!directActionLocks['shot-them-missed']}
                      />
                    )}

                    {/* Ground Balls */}
                    {gameTrackingData.groundBalls && (
                      <GroundBallsSection
                        onWeGroundBall={() => handleWeTurnoverAction('onWeGroundBall')}
                        onTheyGroundBall={() => handleTheyGroundBallOrTurnover('Ground Ball', 'groundball')}
                        actionFeedback={actionFeedback}
                        theyGroundBallDisabled={!!directActionLocks['them-ground-ball']}
                      />
                    )}

                    {/* Turnovers / Caused Turnovers */}
                    {gameTrackingData.turnovers && (
                      <TurnoversSection
                        onWeStoleBall={() => handleWeTurnoverAction('onWeStoleBall')}
                        onWeTurnover={() => handleWeTurnoverAction('onWeTurnover')}
                        onTheyStoleBall={handleTheyStoleBall}
                        onTheyTurnover={handleTheyTurnover}
                        actionFeedback={actionFeedback}
                        theyTurnoverDisabled={!!directActionLocks['them-turnover']}
                      />
                    )}
                    {/* Clear */}
                    {gameTrackingData.clears && (
                      <ClearsSection
                        onWeSuccess={handleWeClearSuccess}
                        onWeFail={handleWeClearFail}
                        onTheySuccess={handleTheyClearSuccess}
                        onTheyFail={handleTheyClearFail}
                        actionFeedback={actionFeedback}
                        disabledButtons={{
                          weSuccess: !!directActionLocks['clear-us-success'],
                          weFail: !!directActionLocks['clear-us-fail'],
                          theySuccess: !!directActionLocks['clear-them-success'],
                          theyFail: !!directActionLocks['clear-them-fail'],
                        }}
                      />
                    )}
                  </div>
                  {/* Draw Control */}
                  {gameTrackingData.faceOffs && (
                    <FaceOffSection
                      midfieldPlayers={midfieldPlayers}
                      otherPlayers={capturePlayers.filter((p) => !midfieldPlayers.includes(p))}
                      drawTakerId={drawTakerId}
                      onDrawTakerChange={setDrawTakerId}
                      onUsFaceOff={openDrawControlWon}
                      onThemFaceOff={handleDrawControlLost}
                      actionFeedback={actionFeedback}
                      themDisabled={!!directActionLocks['draw-control-them']}
                    />
                  )}
                </div>
              </div>
            )}

            {/* Add Stat Button */}
            {isTouchOnly && (
              <div className="justify-end pt-4 pr-4 flex w-full">
                <button
                  type="button"
                  className="bg-[#294e87] text-white font-semibold text-lg px-4 py-2 rounded-lg hover:scale-105"
                  onClick={handleAddStatClick}
                >
                  Add Stat
                </button>
              </div>
            )}
          </>
        )}

      {/* Stat List */}
      <StatsList
        statsData={statsData}
        onRefresh={() => handleRefreshStats()}
        players={players}
        statTypes={STATSTYPE}
        onFilterChange={handleStatsFilterChange}
        selectedPlayer={playerFilter}
        selectedStatType={statTypeFilter}
        period={activeQuarter}
        gameDate={gameDate || undefined}
        gameTime={gameTime || undefined}
        gameTimezone={gameTimezone || undefined}
        onAddStatClick={handleAddStatClick}
      />

      {/* Confetti */}
      {showConfetti && (
        <Confetti
          width={width}
          height={height}
          initialVelocityY={8}
          initialVelocityX={3}
          gravity={0.2}
          wind={0.02}
          friction={0.99}
          numberOfPieces={600}
          recycle={false}
          run={true}
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            width: '100vw',
            height: '100vh',
            pointerEvents: 'none',
            zIndex: 50,
          }}
        />
      )}

      {/* Assist Prompt Modal */}
      <AssistPromptModal
        open={showAssistPromptModal}
        goalScorer={
          goalScorerForAssistPrompt !== null && goalScorerForAssistPrompt !== 'team'
            ? players.find((p) => p.no === goalScorerForAssistPrompt) || null
            : goalScorerForAssistPrompt === 'team'
              ? { firstName: 'Team', lastName: 'Stat', no: 'TEAM' }
              : null
        }
        onResponse={handleAssistPromptResponse}
      />

      {/* US Shot Modal */}
      <PlayerSelectionModal
        open={showShotModal}
        players={capturePlayers}
        title={
          shotModalType === 'goal'
            ? shotModalSide === 'US'
              ? 'WE SCORED!'
              : 'THEY SCORED'
            : shotModalSide === 'US'
              ? shotModalType === 'saved'
                ? 'SHOT SAVED'
                : shotModalType === 'missed'
                  ? 'SHOT MISSED'
                  : shotModalType === 'blocked'
                    ? 'SHOT BLOCKED'
                    : `${shotModalSide} ${(shotModalType || '')?.toUpperCase()}`
              : shotModalType === 'saved'
                ? 'THEIR SHOT SAVED!'
                : shotModalType === 'blocked'
                  ? 'THEIR SHOT BLOCKED!'
                  : `${shotModalSide} ${(shotModalType || '')?.toUpperCase()}`
        }
        subtitle={getShotModalSubtitle(shotModalType, shotModalSide)}
        selectedPlayer={selectedShotPlayer}
        onSelectPlayer={handleShotPlayerSelect}
        onConfirm={() => {}}
        onClose={() => {
          setShowShotModal(false);
          setShotModalSide(null);
          setShotModalType(null);
          setSelectedShotPlayer(null);
          setShotLocationValue(null);
          setThemLocationDeferred(false);
          themShotLocationRef.current = null;
        }}
        showTeamStatButton={shouldShowTeamStatButton('shot', shotModalSide || '', shotModalType || '')}
        onTeamStat={handleTeamStatForShot}
        submitting={selectionSubmitting}
        showLocationPicker={
          isShotChartClient() &&
          (shotModalSide === 'US' ||
            (shotModalSide === 'THEM' && (themLocationDeferred || shotModalType === 'blocked')))
        }
        locationCollapsible={shotModalSide === 'US'}
        locationFirst={shotModalSide === 'THEM' && shotModalType === 'blocked'}
        locationTitle={shotModalSide === 'THEM' ? 'Tap where their shot came from' : undefined}
        locationOutcome={shotModalType}
        locationValue={shotLocationValue}
        onLocationChange={setShotLocationValue}
      />

      {/* Assist Modal */}
      <PlayerSelectionModal
        open={showAssistModal}
        players={capturePlayers}
        title="ASSIST"
        subtitle="Who made the assist?"
        selectedPlayer={selectedAssistPlayerForGoal}
        onSelectPlayer={handleAssistModalSelect}
        onConfirm={() => {
          if (selectedAssistPlayerForGoal !== null) {
            handleAssistModalSelect(selectedAssistPlayerForGoal);
          }
        }}
        onClose={() => {
          setShowAssistModal(false);
          setSelectedAssistPlayerForGoal(null);
          // Also reset shot modal states when assist modal is closed
          setShotModalSide(null);
          setShotModalType(null);
          setSelectedShotPlayer(null);
          // Also reset assist prompt states
          setShowAssistPromptModal(false);
          setGoalScorerForAssistPrompt(null);
        }}
        showTeamStatButton={shouldShowTeamStatButton('assist', 'US')}
        onTeamStat={handleTeamStatForAssist}
        submitting={selectionSubmitting}
      />

      {/* THEM shot location — opens before the goalie / blocker step */}
      <ThemShotLocationSheet
        outcome={themLocationType}
        onDone={handleThemLocationDone}
        onCancel={handleThemLocationCancel}
      />

      {/* THEM Shot Modal */}
      <PlayerSelectionModal
        open={showThemGoalieModal}
        players={getUsGoalies()}
        title={themGoalieType === 'goal' ? 'THEY SCORED' : 'THEIR SHOT SAVED!'}
        subtitle={themGoalieType === 'goal' ? 'Who was our goal keeper?' : 'Who was our goal keeper?'}
        selectedPlayer={selectedThemGoalie as any}
        onSelectPlayer={(val: any) => handleThemGoalieSelect(val)}
        onConfirm={() => {}} // Not used when showConfirmButton is false
        onClose={() => {
          setShowThemGoalieModal(false);
          setThemGoalieType(null);
          setSelectedThemGoalie(null);
          setThemLocationDeferred(false);
          setShotLocationValue(null);
          themShotLocationRef.current = null;
        }}
        submitting={selectionSubmitting}
        showLocationPicker={isShotChartClient() && themLocationDeferred}
        locationCollapsible={false}
        locationOutcome={themGoalieType}
        locationValue={shotLocationValue}
        onLocationChange={setShotLocationValue}
      />

      {/* Game start / mid-game goalie picker */}
      <GoalieSelectModal
        open={showChangeGoalieModal}
        players={getUsGoalies()}
        title={isStartGoaliePrompt ? 'Select Goalie' : 'Change Goalie'}
        subtitle={isStartGoaliePrompt ? 'Who is the goalie for this game?' : 'Who is in net now?'}
        selectedPlayerNo={selectedChangeGoalie}
        onSelectPlayerNo={(val) => setSelectedChangeGoalie(val as any)}
        onConfirm={handleChangeGoalieConfirm}
        requireSelection={isStartGoaliePrompt}
        onClose={() => {
          if (isStartGoaliePrompt) return;
          setShowChangeGoalieModal(false);
          setSelectedChangeGoalie(null);
        }}
        submitting={selectionSubmitting}
      />

      <CardColorModal
        open={!!cardPromptSide}
        onSelect={handleCardColorSelect}
        onClose={() => setCardPromptSide(null)}
      />
      <PenaltyModal
        open={!!showPenaltyModal && penaltySide === 'US'}
        players={capturePlayers}
        side={penaltySide}
        cardColor={pendingCardColor}
        onSelectPenaltyPlayer={handleSelectPenaltyPlayer}
        onClose={() => {
          setShowPenaltyModal(null);
          setPenaltySide(null);
          setSelectedPenaltyPlayers([]);
          setPendingCardColor(null);
        }}
        selectedPlayers={selectedPenaltyPlayers}
        onTeamStat={handleTeamStatForPenalty}
        submitting={penaltySubmitting}
      />
      {/* Turnover Modal */}
      <PlayerSelectionModal
        open={showTurnoverModal}
        players={capturePlayers}
        title={
          turnoverModalSide === 'US'
            ? turnoverModalType === 'onWeGroundBall'
              ? 'GROUND BALL US'
              : turnoverModalType === 'onWeStoleBall'
                ? 'CAUSED TURNOVER US'
                : turnoverModalType === 'onWeTurnover'
                  ? 'TURNOVER US'
                  : 'CAUSED TURNOVER THEM'
            : 'CAUSED TURNOVER THEM'
        }
        subtitle={getTurnoverModalSubtitle(turnoverModalType, turnoverModalSide)}
        selectedPlayer={selectedTurnoverPlayer}
        onSelectPlayer={handleTurnoverPlayerSelect}
        onConfirm={() => {
          if (selectedTurnoverPlayer !== null) {
            handleTurnoverPlayerSelect(selectedTurnoverPlayer);
          }
        }}
        onClose={() => {
          setShowTurnoverModal(false);
          setTurnoverModalType(null);
          setTurnoverModalSide(null);
          setSelectedTurnoverPlayer(null);
        }}
        showShotClockViolationButton={
          shotClockEnabled && turnoverModalSide === 'US' && turnoverModalType === 'onWeTurnover'
        }
        onShotClockViolation={() => {
          // Close the modal first
          setShowTurnoverModal(false);
          setTurnoverModalType(null);
          setTurnoverModalSide(null);
          setSelectedTurnoverPlayer(null);
          // Then handle the shot clock violation
          handleWeShotClockViolation();
        }}
        showTeamStatButton={shouldShowTeamStatButton('turnover', turnoverModalSide || '', turnoverModalType || '')}
        onTeamStat={handleTeamStatForTurnover}
        submitting={selectionSubmitting}
      />
      {/* Draw Control Modal - midfield roster plus "Other Players" */}
      <PlayerSelectionModal
        open={showFaceoffModal}
        players={[...midfieldPlayers, { _id: 'other', isOtherPlayer: true, no: '+', firstName: 'Other', lastName: 'Players' }]}
        title="DRAW CONTROL WON"
        subtitle="Who earned the Draw Control?"
        selectedPlayer={selectedFaceoffPlayer as any}
        onSelectPlayer={async (playerNo: number | string) => {
          setSelectedFaceoffPlayer(playerNo);

          if (playerNo === '+') {
            setShowFaceoffModal(false);
            setShowFaceoffFullRosterModal(true);
            return;
          }

          await handleDrawControlEarned(playerNo);
        }}
        onConfirm={() => {}}
        onClose={closeDrawControl}
        submitting={selectionSubmitting}
      />

      {/* Draw Control Full Roster Modal - for "Other Players" selection */}
      <PlayerSelectionModal
        open={showFaceoffFullRosterModal}
        players={capturePlayers}
        title="DRAW CONTROL WON"
        subtitle="Who earned the Draw Control?"
        selectedPlayer={selectedFaceoffPlayer as any}
        onSelectPlayer={async (playerNo: number | string) => {
          setSelectedFaceoffPlayer(playerNo);
          await handleDrawControlEarned(playerNo);
        }}
        onConfirm={() => {}}
        onClose={closeDrawControl}
        submitting={selectionSubmitting}
      />

      {/* 8M Free Possession - THEM: who committed the foul */}
      <PlayerSelectionModal
        open={showEightMeterFoulerModal}
        players={capturePlayers}
        title="8M FREE POSSESSION - THEM"
        subtitle="Who made the Foul?"
        selectedPlayer={null}
        onSelectPlayer={handleEightMeterFoulerSelect}
        onConfirm={() => {}}
        onClose={resetEightMeterFlow}
        showTeamStatButton
        onTeamStat={handleEightMeterFoulerTeamStat}
      />
      <EightMeterFoulModal
        open={showEightMeterFoulModal}
        submitting={!!directActionLocks['eight-meter-US'] || !!directActionLocks['eight-meter-THEM']}
        onConfirm={handleEightMeterFoulConfirm}
        onClose={resetEightMeterFlow}
      />
      {/* Add Stat Modal */}
      <AddStatModal
        open={showAddStatModal}
        onClose={() => setShowAddStatModal(false)}
        onSuccess={() => {
          setShowAddStatModal(false);
          handleRefreshStats();
        }}
        players={players}
        statTypes={STATSTYPE}
        period={activeQuarter}
        usPenalties={usPenalty}
        themPenalties={themPenalty}
      />

      {/* Shot Clock Violation Modal */}
      <ShotClockViolationModal
        isOpen={showShotClockModal}
        onClose={() => setShowShotClockModal(false)}
        onYes={handleTheyShotClockViolationYes}
        onNo={handleTheyShotClockViolationNo}
        submitting={!!directActionLocks['shot-clock-them-yes'] || !!directActionLocks['shot-clock-them-no']}
      />

      {/* Data Entry Quarter Warning Modal */}
      <DataEntryQuarterWarningModal
        isOpen={showDataEntryWarning}
        currentQuarter={activeQuarter || ''}
        mostRecentQuarter={mostRecentQuarterWithData || ''}
        onProceed={handleDataEntryProceed}
        onSwitchQuarter={handleDataEntrySwitchQuarter}
      />

      {/* Tied Game Prompt Modal
      <TiedGamePromptModal
        isOpen={showTiedGamePrompt}
        gameScores={gameScores}
        onResponse={handleTiedGameResponse}
      /> */}
    </div>
  );
};

export default StatsPage;
